import {
  AgenticDecision,
  AgenticMatch,
  BuildingCandidate,
  HmlrMatch,
  Lead,
  LeadsBulkPremiumEnrichOperationResult,
  SelectedOrg,
  TenantMatch,
  useLeadSearch,
  useLeads,
  useOperation,
  useSite,
  useSiteList,
} from "@inrange/building-manager-api-client";
import { getBuildingCoordinates } from "@inrange/building-manager-api-client/models-site";
import SortBySelect from "./SortBySelect";
import {
  SortDirection,
  buildSortOptions,
  nextSortDirection,
} from "./sortOptions";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EnrichmentTierBadge } from "./EnrichmentTierBadge";
import LeadEditPanel, {
  InputContext,
  OrgDecisionPatch,
  SavePayload,
} from "./LeadEditPanel";
import { BulkCreateSitesModal } from "./BulkCreateSitesModal";
import { useBulkCreateSites } from "./bulkCreateSites";
import LeadsHelpModal from "./LeadsHelpModal";
import {
  leadValueBadgeProps,
  leadValueLabel,
  leadValueSortWeight,
  leadValueTooltip,
  tenantCommercialPending,
} from "./leadValueDisplay";
import { useSettleCommercialPending } from "./useSettleCommercialPending";
import { WithTooltip } from "./WithTooltip";
import LeadsOverviewMap, { OverviewPin } from "./LeadsOverviewMap";
import {
  PREMIUM_ENRICHMENT_SERVICES,
  formatEtaSeconds,
} from "./enrichmentCopy";
import { OWNER_OCCUPIED_LANDLORD_LABEL } from "./leadDisplay";
import {
  agenticDisplayName,
  researchedBlankLandlordStatus,
} from "./leadResearchDisplay";
import { bboxesOverlap, polygonBbox, polygonsOverlap } from "./leadGeo";
import {
  planBulkResearchContinuation,
  researchRowChipState,
  type BulkResearchResultShape,
} from "./leadResearchProgress";
import {
  loadPersistedResearchOp,
  savePersistedResearchOp,
} from "./researchOpStorage";
import { useIsCompactLayout } from "./useIsCompactLayout";
import { useLiveResearchRuns } from "./useLiveResearchRuns";
import { formatAddressCasing } from "./addressCasing";

type SortKey =
  | "address"
  | "leadConfidence"
  | "locationConfidence"
  | "buildingConfidence"
  | "combinedBuildingConfidence"
  | "surfaceAreaSqm"
  | "tenantConfidence"
  | "tenantName"
  | "leadValue"
  | "landlordConfidence"
  | "landlordName"
  | "createdAt";

// Both directions of every sortable column, so a header click always lands on a
// named option - see SortBySelect for why there is no "Column sort" fallback.
// "Value then confidence" is the leadValue-desc option: the comparator settles
// ties inside a value band by confidence, which is the operator's real question
// on this board (value answers it coarsely, confidence settles it inside a band).
const SORT_OPTIONS = buildSortOptions<SortKey>([
  {
    key: "leadValue",
    descLabel: "Value then confidence",
    ascLabel: "Value (lowest first)",
  },
  {
    key: "leadConfidence",
    descLabel: "Confidence (highest)",
    ascLabel: "Confidence (lowest)",
  },
  { key: "address", descLabel: "Address (Z-A)", ascLabel: "Address (A-Z)" },
  {
    key: "surfaceAreaSqm",
    descLabel: "Building size (largest)",
    ascLabel: "Building size (smallest)",
  },
  {
    key: "tenantConfidence",
    descLabel: "Tenant confidence (highest)",
    ascLabel: "Tenant confidence (lowest)",
  },
  { key: "tenantName", descLabel: "Tenant (Z-A)", ascLabel: "Tenant (A-Z)" },
  {
    key: "landlordConfidence",
    descLabel: "Landlord confidence (highest)",
    ascLabel: "Landlord confidence (lowest)",
  },
  {
    key: "landlordName",
    descLabel: "Landlord (Z-A)",
    ascLabel: "Landlord (A-Z)",
  },
  { key: "createdAt", descLabel: "Last added", ascLabel: "First added" },
]);

const DEFAULT_SORT: { key: SortKey; dir: SortDirection } = {
  key: "leadValue",
  dir: "desc",
};

// Mirrors `combinedBuildingConfidence` in LeadSearchPage - same min-of-
// two-signals semantics, so /leads and /leads/search show the same
// number on the same row.
const combinedBuildingConfidence = (
  locationConfidence: number | null,
  buildingConfidence: number | null
): number | null => {
  if (locationConfidence == null && buildingConfidence == null) return null;
  if (locationConfidence == null) return buildingConfidence;
  if (buildingConfidence == null) return locationConfidence;
  return Math.min(locationConfidence, buildingConfidence);
};

const buildTenantTooltip = (
  match: { source?: string | null; confidence: number | null } | null
): string => {
  if (!match) return "No tenant match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const sourceLine = (() => {
    switch (match.source) {
      case "ch":
        return "Companies House: postcode-keyed search; ranked by SIC-code match, active status, address fuzziness.";
      case "osm":
        return "OpenStreetMap: tagged-business proximity to the building polygon.";
      case "google_places":
        return "Google Places: nearby-search around the building; ranked by distance + name similarity.";
      default:
        return "Tenant match from upstream pipeline.";
    }
  })();
  return `Tenant: ${conf}\n${sourceLine}`;
};

const buildLandlordTooltip = (
  match: { confidence: number | null; distanceMetres?: number | null } | null
): string => {
  if (!match) return "No landlord match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const distLine =
    match.distanceMetres != null
      ? `Deed centroid ${Math.round(match.distanceMetres)}m from building polygon.`
      : null;
  return [
    `Landlord: ${conf}`,
    "HMLR title deed, polygon-filtered to deeds overlapping the selected building.",
    distLine,
  ]
    .filter(Boolean)
    .join("\n");
};

const LOCATION_CONFIDENCE_BY_LABEL: Record<string, number> = {
  HIGH: 95,
  MEDIUM: 70,
  LOW: 35,
  POSTCODE_CENTROID: 15,
};
// Mirrors `locationConfidenceForCandidate` in LeadSearchPage: a
// geocoder match alone isn't useful if no building polygon was
// found. Return null when polygon_candidates is empty so the
// combined building confidence collapses to "-" rather than
// reporting a high location number on a row the operator can't
// act on.
const locationConfidenceForLead = (lead: Lead): number | null => {
  const polygonData = (lead.polygonData ?? {}) as Record<string, unknown>;
  const polygonCandidates =
    (polygonData.polygon_candidates as Array<unknown> | undefined) ?? [];
  if (polygonCandidates.length === 0) return null;
  const geocodes =
    (polygonData.geocodes as Array<Record<string, unknown>> | undefined) ?? [];
  const label = (
    (geocodes[0]?.confidence_label as string | undefined) ?? ""
  ).toUpperCase();
  return label ? (LOCATION_CONFIDENCE_BY_LABEL[label] ?? null) : null;
};

// The inbox page's in-flight single-research op, persisted per-tab so a refresh
// re-attaches the poll rather than forgetting the run (see researchOpStorage).
// Distinct storage key from the search page - the two carry different row keys.
interface PersistedInboxResearchOp {
  operationKey: string;
  siteId: string;
  leadId: string;
}
const INBOX_RESEARCH_OP_STORAGE_KEY = "inrange.leads.inboxResearchOp";
// Every landlord-research operation is filed under one fixed site id - mirrors
// LEADS_RESEARCH_SITE_ID in lambda_handlers/long_ops/operation_landlord_research.py.
// Needed to poll a run this tab did not launch: the row carries the operation key,
// but no site id (a lead is not a site).
const LEADS_RESEARCH_SITE_ID = "leads-research";
// The operation type of a SINGLE-subject research run. A bulk op's progress counts
// whichever row that batch is working, so its count must never be shown on another
// row of the batch - hence the type gate below.
const SINGLE_RESEARCH_OPERATION_TYPE = "landlord_research";

const SortHeader: React.FC<{
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDirection };
  setSort: (s: { key: SortKey; dir: SortDirection }) => void;
  tooltip?: string;
}> = ({ label, sortKey, sort, setSort, tooltip }) => {
  const active = sort.key === sortKey ? sort.dir : null;
  // Desc <-> asc, with no unsorted third state: the "Sort by" dropdown and
  // these headers drive one sort, and an unsorted state is one the dropdown
  // could not name (see SortBySelect).
  const onClick = () =>
    setSort({ key: sortKey, dir: nextSortDirection(sortKey, sort) });
  const arrow = active === "desc" ? " ▼" : active === "asc" ? " ▲" : "";
  return (
    <th
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={onClick}
      title={tooltip}
    >
      {label}
      {arrow}
    </th>
  );
};

const ConfidenceBadge: React.FC<{
  confidence: number | null;
  tooltip?: string;
}> = ({ confidence, tooltip }) => {
  const dash =
    confidence == null ? (
      <span
        style={{
          color: "#aaa",
          fontSize: 12.5,
          cursor: tooltip ? "help" : undefined,
        }}
      >
        -
      </span>
    ) : null;
  if (confidence == null) {
    if (!tooltip) return dash;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip style={{ whiteSpace: "pre-line", textAlign: "left" }}>
            {tooltip}
          </Tooltip>
        }
      >
        {dash as React.ReactElement}
      </OverlayTrigger>
    );
  }
  const colour =
    confidence >= 70 ? "#28a745" : confidence >= 40 ? "#ffc107" : "#6c757d";
  const badge = (
    <span
      style={{
        marginLeft: 4,
        padding: "0 4px",
        background: colour,
        color: "#fff",
        fontSize: 11.5,
        borderRadius: 3,
        cursor: tooltip ? "help" : undefined,
      }}
    >
      {Math.round(confidence)}%
    </span>
  );
  if (!tooltip) return badge;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip style={{ whiteSpace: "pre-line", textAlign: "left" }}>
          {tooltip}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
};

interface LeadRow {
  lead: Lead;
  buildingCandidates: BuildingCandidate[];
  hmlrMatches: HmlrMatch[];
  // Polygon-overlap-filtered HMLR deeds (matches the right-panel's
  // visible options + the saved-landlord confidence lookup), so the
  // mid-edit overlay derives a pending landlord's confidence off the
  // same list the saved pick will use.
  filteredHmlrMatches: HmlrMatch[];
  tenantMatches: TenantMatch[];
  // Agentic-research landlord candidates (from `agenticData.matches`), used to
  // resolve the confidence of an unsaved agentic landlord pick in the left
  // table - they never appear in `hmlrMatches`.
  agenticMatches: AgenticMatch[];
  locationConfidence: number | null;
  buildingConfidence: number | null;
  tenantName: string | null;
  tenantConfidence: number | null;
  landlordName: string | null;
  landlordConfidence: number | null;
  initialBuildingBkey: string | null;
  initialLandlord: SelectedOrg | null;
  initialTenant: SelectedOrg | null;
  // Carried over from the VOA row at promote time (see BMA's
  // `_promote_one`'s tenantData.premiumEnrichedAt). Drives:
  // - The $ adorner next to the address (mirrors /leads/search's
  //   EnrichmentTierBadge usage).
  // - The "premium may help" vs "premium ran with no result" message
  //   in `issuesForRow` so the operator gets accurate guidance.
  premiumEnrichedAt: string | null;
}

// Bulk-create per-row org choice. One per (leadId, role): the operator
// either accepts the BE's default plan (`null` here, derived from the
// preview), maps the role to a specific existing platform org, or
// asks for a brand-new org with a chosen name. The latter still goes
// through the BE's fuzzy-match safety net - if the chosen name
// matches an existing org by `_normalise_name` rules, the BE silently
// reuses it.

// Recover the per-match confidence for a tenant/landlord SelectedOrg by
// looking it up against the source match list by its source-keyed
// identifier. Shared by the saved-decision derivation in `buildLeadRow`
// and the mid-edit (unsaved) overlay in the left table, so a pending
// pick shows the same confidence the saved pick will. platform/manual
// picks are explicit operator choices with no candidate-confidence, so
// they report 100 (mirrors the left-table convention). Returns null
// when the selection can't be matched to any candidate.
const deriveTenantConfidence = (
  sel: SelectedOrg | null | undefined,
  tenantMatches: TenantMatch[]
): number | null => {
  if (!sel) return null;
  if (sel.source === "platform" || sel.source === "manual") return 100;
  for (const m of tenantMatches) {
    if (sel.chCompanyNumber && m.companyNumber === sel.chCompanyNumber) {
      return m.confidence;
    }
    if (sel.googlePlaceId && m.googlePlaceId === sel.googlePlaceId) {
      return m.confidence;
    }
    if (sel.osmId && m.osmId === sel.osmId) {
      return m.confidence;
    }
    if (m.companyName === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};

const deriveLandlordConfidence = (
  sel: SelectedOrg | null | undefined,
  hmlrMatches: HmlrMatch[]
): number | null => {
  if (!sel) return null;
  if (sel.source === "platform" || sel.source === "manual") return 100;
  for (const m of hmlrMatches) {
    if (sel.hmlrTitleNumber && m.titleNumber === sel.hmlrTitleNumber) {
      return m.confidence;
    }
    if (m.proprietorName === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};

// Agentic-research landlord candidates live in `agenticData.matches`, never in
// `hmlrMatches` (the eligibility contract keeps them out of the auto-pick
// paths), so an explicitly-picked agentic landlord's confidence is resolved
// from that list instead - matched by display name, since agentic picks carry
// no titleNumber. Mirrors `agenticConfidenceForSelection` on /leads/search so
// the left column and right panel agree on an unsaved agentic pick.
const deriveAgenticConfidence = (
  sel: SelectedOrg | null | undefined,
  agenticMatches: AgenticMatch[]
): number | null => {
  if (!sel) return null;
  for (const m of agenticMatches) {
    if (agenticDisplayName(m) === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};

const buildLeadRow = (lead: Lead): LeadRow => {
  const polygonData = (lead.polygonData ?? {}) as Record<string, unknown>;
  const polygonCandidatesRaw =
    (polygonData.polygon_candidates as unknown[] | undefined) ?? [];
  const buildingCandidates: BuildingCandidate[] = polygonCandidatesRaw.map(
    (raw) => {
      // camelCase shape, matches `BuildingCandidate` on the wire.
      // Both VOA-promoted and CSV-imported leads write this shape now;
      // the legacy snake_case shape (with `geometry.coordinates`,
      // `centroid: [lat, lon]`, `surface_area_sqm`, `score_breakdown`)
      // was migrated over via the same alignment work. If you find
      // a row that still has the legacy shape, run the migration
      // script (`scripts/Y2026_Q2/realign_lead_polygon_candidates.py`).
      //
      // Spread first so optional fields the BE adds later
      // (`belowAreaMatchFloor`, `pinOverlapsPolygon`,
      // `matchedExistingSiteID`, `matchedExistingSiteName`, ...) flow
      // through automatically. Then explicit overrides for the
      // required fields with defaults for legacy nulls. Without the
      // spread, every new optional field on `BuildingCandidate` has
      // to be added here by hand and silently breaks any badge that
      // reads it on CSV-imported leads vs VOA-search candidates.
      const r = raw as Record<string, unknown>;
      return {
        ...(raw as BuildingCandidate),
        bkey: r.bkey as string,
        polygon: r.polygon as BuildingCandidate["polygon"],
        centroidLat: r.centroidLat as number,
        centroidLon: r.centroidLon as number,
        surfaceAreaSqm: (r.surfaceAreaSqm as number | null) ?? null,
        distanceMetres: (r.distanceMetres as number | null) ?? 0,
        confidence: (r.confidence as number | null) ?? null,
        score: (r.score as number | null) ?? null,
        proximityWeight: (r.proximityWeight as number | null) ?? null,
        areaWeight: (r.areaWeight as number | null) ?? null,
        areaMatch: (r.areaMatch as number | null) ?? null,
      };
    }
  );

  const hmlr = (lead.hmlrData ?? {}) as Record<string, unknown>;
  const hmlrMatches = (hmlr.matches as HmlrMatch[] | undefined) ?? [];

  const ch = (lead.tenantData ?? {}) as Record<string, unknown>;
  const tenantMatches = (ch.matches as TenantMatch[] | undefined) ?? [];

  const agentic = (lead.agenticData ?? {}) as Record<string, unknown>;
  const agenticMatches = (agentic.matches as AgenticMatch[] | undefined) ?? [];

  const polyDecision = lead.polygonDecision as {
    payload?: { acceptedBkey?: string };
  } | null;
  const initialBuildingBkey =
    polyDecision?.payload?.acceptedBkey ?? buildingCandidates[0]?.bkey ?? null;

  const buildingConfidence = buildingCandidates[0]?.confidence ?? null;

  const ll = lead.landlordDecision as { payload?: SelectedOrg } | null;
  const tn = lead.tenantDecision as { payload?: SelectedOrg } | null;
  const initialLandlord = ll?.payload ?? null;
  const initialTenant = tn?.payload ?? null;

  // Match the right-panel filter (`filteredHmlrMatches` in
  // LeadEditPanel): only show landlords whose deed polygon overlaps
  // the auto-selected building. Otherwise the left-table Landlord
  // column claimed a deed the right panel hides, and operators saw
  // a "not in current matches" warning in the right panel against a
  // row that read fine on the left.
  const selectedBuilding =
    buildingCandidates.find((b) => b.bkey === initialBuildingBkey) ??
    buildingCandidates[0];
  const selectedBuildingBbox = selectedBuilding
    ? polygonBbox(selectedBuilding.polygon)
    : null;
  const filteredHmlrMatches = hmlrMatches.filter((m) => {
    if (!selectedBuilding || !selectedBuildingBbox) return true;
    if (!m.polygon) return true;
    const mb = m.bbox ?? polygonBbox(m.polygon);
    if (mb && !bboxesOverlap(selectedBuildingBbox, mb)) return false;
    return polygonsOverlap(selectedBuilding.polygon, m.polygon, {
      lat: selectedBuilding.centroidLat,
      lon: selectedBuilding.centroidLon,
    });
  });
  const topLandlordInFilter = filteredHmlrMatches[0] ?? null;

  // Saved decisions only display if they're still in the current
  // filtered match list - otherwise we'd show a stale "selected"
  // value the right panel doesn't acknowledge. The migration script
  // clears these server-side too; this is the FE-side guard for
  // freshly-stale rows.
  //
  // Platform / manual selections aren't bound to the source-side
  // match lists (they're operator-typed or platform-org picks), so
  // they're always valid. Without this carve-out, picking a platform
  // org and hitting Save makes the selection vanish on next render
  // because the left-table cell and the Create-site preview both
  // re-derive from the top source-match.
  const savedLandlordValid =
    initialLandlord != null &&
    (initialLandlord.source === "platform" ||
      initialLandlord.source === "manual" ||
      // Agentic-research picks live in `agenticMatches`, not the HMLR deeds, so
      // validate a saved agentic landlord against that list (mirrors
      // `savedLandlordIsValid` on /leads/search). Without this the inbox Landlord
      // column dropped a researched landlord the right panel still shows (it fell
      // through to the top HMLR deed, or "-" when there was none).
      (initialLandlord.source === "agentic" &&
        agenticMatches.some(
          (m) => agenticDisplayName(m) === initialLandlord.displayName
        )) ||
      filteredHmlrMatches.some(
        (m) =>
          m.proprietorName === initialLandlord.displayName ||
          m.titleNumber === initialLandlord.hmlrTitleNumber
      ));
  const savedTenantValid =
    initialTenant != null &&
    (initialTenant.source === "platform" ||
      initialTenant.source === "manual" ||
      tenantMatches.some(
        (m) =>
          m.companyName === initialTenant.displayName ||
          (m.googlePlaceId != null &&
            m.googlePlaceId === initialTenant.googlePlaceId)
      ));

  // For saved selections, recover the real per-match confidence
  // from the original candidate list so the left-panel badge agrees
  // with the right-panel match-table value. SelectedOrg doesn't
  // carry confidence on the wire so we look it up by source-keyed
  // identifier (CH number / Places ID / OSM ID / proprietor name).
  // Hard-coding 100 was wrong: it claimed perfect confidence for
  // any saved pick, even ones the operator chose at e.g. 60%.
  //
  // Two carve-outs to that rule:
  // - source='platform': the operator either picked an existing
  //   platform org from the typeahead or accepted the portfolio
  //   default at /leads/import. Either way it's an explicit,
  //   intentional choice with no candidate-confidence to derive from.
  // - source='manual': operator typed a new org name. Explicit.
  // Both report as 100% so the left-table confidence column reflects
  // the operator's confidence, not a (missing) enrichment match.
  const savedTenantConfidence = savedTenantValid
    ? deriveTenantConfidence(initialTenant, tenantMatches)
    : null;
  const savedLandlordConfidence = savedLandlordValid
    ? initialLandlord!.source === "agentic"
      ? deriveAgenticConfidence(initialLandlord, agenticMatches)
      : deriveLandlordConfidence(initialLandlord, filteredHmlrMatches)
    : null;

  return {
    lead,
    buildingCandidates,
    hmlrMatches,
    filteredHmlrMatches,
    tenantMatches,
    agenticMatches,
    locationConfidence: locationConfidenceForLead(lead),
    buildingConfidence,
    tenantName: savedTenantValid
      ? initialTenant!.displayName
      : (tenantMatches[0]?.orgName ?? tenantMatches[0]?.companyName ?? null),
    tenantConfidence: savedTenantValid
      ? savedTenantConfidence
      : (tenantMatches[0]?.confidence ?? null),
    landlordName: savedLandlordValid
      ? initialLandlord!.displayName
      : (topLandlordInFilter?.orgName ??
        topLandlordInFilter?.proprietorName ??
        null),
    landlordConfidence: savedLandlordValid
      ? savedLandlordConfidence
      : (topLandlordInFilter?.confidence ?? null),
    initialBuildingBkey,
    initialLandlord,
    initialTenant,
    premiumEnrichedAt:
      typeof ch.premiumEnrichedAt === "string" ? ch.premiumEnrichedAt : null,
  };
};

// Build the per-pass freshness footer the right-panel renders under
// "Data fetched". Mirrors what /leads/search passes for VOA
// candidates; sourced from polygonData.geocodedAt /
// polygonData.buildingResolvedAt / hmlrData.fetchedAt /
// tenantData.fetchedAt (set on promotion + CSV import).
const buildEnrichmentFetchedAt = (
  lead: Lead
): {
  geocodedAt: string | null;
  buildingResolvedAt: string | null;
  hmlrEnrichedAt: string | null;
  tenantEnrichedAt: string | null;
  epcEnrichedAt: string | null;
  premiumEnrichedAt: string | null;
} => {
  const polygonData = (lead.polygonData ?? {}) as Record<string, unknown>;
  const hmlr = (lead.hmlrData ?? {}) as Record<string, unknown>;
  const tenant = (lead.tenantData ?? {}) as Record<string, unknown>;
  const isoOrNull = (v: unknown): string | null =>
    typeof v === "string" ? v : null;
  return {
    geocodedAt: isoOrNull(polygonData.geocodedAt),
    buildingResolvedAt: isoOrNull(polygonData.buildingResolvedAt),
    hmlrEnrichedAt: isoOrNull(hmlr.fetchedAt),
    tenantEnrichedAt: isoOrNull(tenant.fetchedAt),
    // EPC + premium timestamps aren't propagated onto promoted Leads
    // today (the lead detail view doesn't have a re-enrich button), so
    // we surface null here. If we ever copy these across on promotion,
    // this is the place to read them.
    epcEnrichedAt: null,
    premiumEnrichedAt: isoOrNull(tenant.premiumEnrichedAt),
  };
};

const buildLeadInputContext = (lead: Lead): InputContext => {
  // Source label: VOA leads come from voa_rating_list (sourceRef is the
  // VOA Assessment Reference); CSV-imported leads come in as
  // `csv_import` with sourceRef "<filename>:<row_index>". Surface the
  // filename so the operator can trace back to the input file.
  let sourceLabel = `VOA · ${lead.sourceRef}`;
  if (lead.source === "csv_import") {
    const filename =
      lead.sourceRef.split(":").slice(0, -1).join(":") || lead.sourceRef;
    sourceLabel = `CSV · ${filename}`;
  }

  // Geocode summary lives inside polygonData for both paths.
  const polygonData = (lead.polygonData ?? {}) as Record<string, unknown>;
  const geocodes =
    (polygonData.geocodes as Array<Record<string, unknown>> | undefined) ?? [];
  const g0 = geocodes[0];
  const geocodeSummary = g0
    ? [g0.source, g0.confidence_label].filter(Boolean).join(" ")
    : null;
  const geocodeAttempts =
    (polygonData.geocodeAttempts as
      | Array<{
          source: string;
          confidenceLabel: string | null;
          lat: number | null;
          lon: number | null;
          wasPicked: boolean;
        }>
      | null
      | undefined) ?? null;

  return {
    sourceLabel,
    address: lead.address,
    declaredFloorAreaSqm: lead.floorAreaSqm,
    declaredNumBuildings: lead.declaredNumBuildings,
    geocodeSummary: geocodeSummary || null,
    geocodeAttempts,
    // CSV-import path persists `polygonData.addressVerdict`; pass
    // straight through so the right panel can render the verdict
    // badge + inferred/unconfirmed-component breakdown. VOA-imported
    // leads don't have it.
    addressVerdict:
      (polygonData.addressVerdict as InputContext["addressVerdict"]) ?? null,
    // Whole Lead JSON for the "View raw source data" modal. Includes
    // sourcePayload (the original CSV row for csv_import leads, or the
    // VOA assessment record), polygonData, hmlrData, tenantData - the
    // full debug picture.
    rawSource: lead as unknown as Record<string, unknown>,
  };
};

const LeadsPage: React.FC = () => {
  document.title = "Leads";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {
    fetchLeads,
    updateLeadDecisions,
    bulkUpdateLeadTags,
    bulkDeleteLeads,
    refetchLead,
    premiumEnrichLead,
    reenrichLeadAddress,
    bulkPremiumEnrichLeads,
    researchLeadLandlord,
    bulkResearchLeadLandlords,
    cancelResearchRow,
  } = useLeads();
  const { deleteSite } = useSite({ app: "admin" });
  // Warm the portfolio list here, where nothing on this page needs it: the "Near this
  // site" typeahead on /leads/search is the slowest thing on that page to arrive, and
  // an operator who opens the inbox is usually one click away from running a search.
  // Same ["sites"] query key + 5-minute staleTime as the picker itself, so this is a
  // prefetch rather than a second request.
  useSiteList();
  // Agentic landlord research on the inbox panel (mirrors LeadSearchPage) so an
  // operator can research a lead in place while triaging. Per-lead LRO; on
  // completion we invalidate the lead so its agenticData shows in the panel.
  // Lazy-init from sessionStorage so a refresh mid-research re-attaches the poll
  // (mirrors LeadSearchPage; keyed by leadId here). Persisted on change below.
  const [researchOp, setResearchOp] = useState<PersistedInboxResearchOp | null>(
    () =>
      loadPersistedResearchOp<PersistedInboxResearchOp>(
        INBOX_RESEARCH_OP_STORAGE_KEY,
        "leadId"
      )
  );
  useEffect(() => {
    savePersistedResearchOp(INBOX_RESEARCH_OP_STORAGE_KEY, researchOp);
  }, [researchOp]);
  // Bulk landlord research over the multi-selected leads (mirrors LeadSearchPage's
  // toolbar action). One LRO for the batch; on completion we invalidate the leads
  // cache so each researched lead's agenticData shows.
  // `leadIds` carries the batch's leads so the right panel can show
  // "researching" (and disable the single-row button) for whichever of them the
  // operator opens while the batch is still running.
  const [bulkResearchOp, setBulkResearchOp] = useState<{
    operationKey: string;
    siteId: string;
    // The leads the BE actually queued for THIS sub-batch (drives the per-row
    // chip via the worker cursor).
    leadIds: string[];
    // Every lead this sub-batch was asked to research (= the remaining chain at
    // launch). `inputIds \ leadIds` is the overflow auto-continue picks up later
    // (shown "queued"); the no-progress guard compares notProcessed vs inputIds.
    inputIds: string[];
    // Leads the operator has pulled from the queue via the panel's per-row Cancel.
    // Optimistic: added on click so the row drops "queued" immediately; the BE append +
    // worker skip make it durable. Resets with the op when the batch ends.
    cancelledRefs: Set<string>;
  } | null>(null);
  // Leads a finished auto-continue chain never reached (no-progress guard tripped,
  // or every remaining lead failed) - a persistent "not run - run again" chip.
  const [researchNotRunRefs, setResearchNotRunRefs] = useState<Set<string>>(
    new Set()
  );
  const [researchError, setResearchError] = useState<string | null>(null);

  const handleLeadRefetch = async () => {
    if (!selectedId) return;
    await refetchLead.mutateAsync(selectedId);
  };
  const handleLeadPremiumEnrich = async () => {
    if (!selectedId) return;
    await premiumEnrichLead.mutateAsync(selectedId);
  };
  const handleReenrichAddress = async (address: string) => {
    if (!selectedId) return;
    await reenrichLeadAddress.mutateAsync({ leadId: selectedId, address });
  };
  const handleResearchLandlord = async () => {
    if (!selectedId) return;
    const leadId = selectedId;
    setResearchError(null);
    // Researching this lead clears any stale "not run" marker on it.
    setResearchNotRunRefs((prev) => {
      if (!prev.has(leadId)) return prev;
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
    // Show THIS lead as researching from the click, until its own row comes back
    // carrying the server key - from then on the row is what says so, for every run
    // at once. Marked before the request so the optimism names the launched lead
    // rather than whichever lead happens to be open while a kickoff is in flight.
    markResearchLaunched(leadId);
    try {
      const res = await researchLeadLandlord.mutateAsync(leadId);
      // Kept for THIS run's live search count and its failure message. Runs go in
      // parallel, so a later launch replaces it - which is now harmless: nothing
      // about a row's state depends on this handle.
      setResearchOp({
        operationKey: res.operationKey,
        siteId: res.siteId,
        leadId,
      });
    } catch (err) {
      unmarkResearchLaunched(leadId);
      setResearchError(
        err instanceof Error
          ? `Could not start landlord research: ${err.message}`
          : "Could not start landlord research."
      );
    }
  };

  const rows = useMemo(
    () => (fetchLeads.data?.leads ?? []).map(buildLeadRow),
    [fetchLeads.data]
  );

  // Leads the BACKEND says have a live research run - the truth for the chip and
  // the panel, whoever launched it. Rows in this page's own bulk sub-batch are left
  // out: that op is already polled, and it does its own per-lead refresh as the
  // worker advances (a duplicate here would just re-invalidate every lead twice).
  const liveResearchRefs = useMemo(() => {
    const ownBulk = new Set(bulkResearchOp?.leadIds ?? []);
    return new Set(
      rows
        .filter((r) => r.lead.researchOperationKey && !ownBulk.has(r.lead.id))
        .map((r) => r.lead.id)
    );
  }, [rows, bulkResearchOp]);
  // Keep those runs' state true while they finish: poll the list while any is in
  // flight, and refresh a lead the moment its run ends.
  const {
    launchedRefs: singleResearchingRefs,
    markLaunched: markResearchLaunched,
    unmarkLaunched: unmarkResearchLaunched,
  } = useLiveResearchRuns({
    liveRefs: liveResearchRefs,
    refresh: () => queryClient.invalidateQueries(["leads"]),
    onFinished: (leadId) => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", leadId]);
    },
  });

  // Rows whose tenant has not been looked up yet render as "Calculating". The
  // list read never fetches, so this is what actually settles them - one LRO
  // for the page, then a single re-read.
  const commercialPendingLeadIds = useMemo(
    () =>
      rows
        .filter((r) => tenantCommercialPending(r.lead.tenantCommercial))
        .map((r) => r.lead.id),
    [rows]
  );
  const { recount: recountCommercial, resolving: recountingCommercial } =
    useSettleCommercialPending({
      leadIds: commercialPendingLeadIds,
      onSettled: () => queryClient.invalidateQueries(["leads"]),
    });
  // BE backlinks each lead to any sites whose `originatedFromLeadId`
  // matches it. Lets the table render a [Site] button on rows that
  // already have a site created from them. Memoised so downstream
  // `useMemo`s keyed off it don't re-fire on every render (default
  // `?? {}` would create a new object literal each pass).
  const siteIdsByLeadId = useMemo(
    () => fetchLeads.data?.siteIdsByLeadId ?? {},
    [fetchLeads.data]
  );
  // Per-lead linked-site building area (m²), used by the left table to
  // show a created/matched site's real footprint instead of the lead's
  // VOA/OSM candidate area. Same `?? {}`-once memo rationale as above.
  const siteBuildingAreaByLeadId = useMemo(
    () => fetchLeads.data?.siteBuildingAreaByLeadId ?? {},
    [fetchLeads.data]
  );
  // Per-lead count of contacts an outreach email has been drafted for (threads,
  // not reruns) - the Emails column. Same `?? {}`-once memo rationale as above.
  const outreachContactCountByLeadId = useMemo(
    () => fetchLeads.data?.outreachContactCountByLeadId ?? {},
    [fetchLeads.data]
  );
  // Per-lead linked-site existing solar (kWp), present only when non-zero. The
  // derived half of the Has existing PV flag - the panel renders the toggle on
  // and read-only for these leads.
  const siteExistingPvKwpByLeadId = useMemo(
    () => fetchLeads.data?.siteExistingPvKwpByLeadId ?? {},
    [fetchLeads.data]
  );
  // Per-lead linked-site InRange install capacity (kWp) - the system we would
  // install, not the existing array above. Shown in the read-only site block
  // under the outreach drafter's own label.
  const siteInstallCapacityKwpByLeadId = useMemo(
    () => fetchLeads.data?.siteInstallCapacityKwpByLeadId ?? {},
    [fetchLeads.data]
  );

  // Selected row in the right panel.
  const selectedId = searchParams.get("selected");
  const selectedRow = rows.find((r) => r.lead.id === selectedId) ?? null;

  // The right panel's `key` used to carry the lead's `updatedAt`, so that the BE
  // rewriting a row re-initialises the panel's props-seeded state. But the panel is
  // its own scroll container and mounts a Leaflet map, so remounting it for a write IT
  // just made throws the operator back to the top of the panel with every cell
  // collapsed. That was survivable while saving was one deliberate click at the end;
  // the Ownership cells now write on each pick (`onSaveOrgDecision` below), which
  // would make the panel jump on every org click.
  //
  // So the key stamp does not advance for the per-pick ORG writes. It still advances
  // for the explicit Save (which relies on the remount to clear per-mount state) and
  // for any rewrite this page did not make. Tracked as a SET of our own stamps rather
  // than just the newest: two writes can overlap and their refetches can land out of
  // order, so "is this one of ours?" can't be answered by the last one alone.
  const selfSavedUpdatedAtsRef = useRef<Set<string>>(new Set());
  // Advanced during render, because the key has to be right on the very render that
  // first shows the new data.
  const panelKeyRef = useRef<{ leadId: string; stamp: string } | null>(null);
  if (selectedRow) {
    const seen = panelKeyRef.current;
    if (seen === null || seen.leadId !== selectedRow.lead.id) {
      selfSavedUpdatedAtsRef.current = new Set();
      panelKeyRef.current = {
        leadId: selectedRow.lead.id,
        stamp: selectedRow.lead.updatedAt,
      };
    } else if (
      seen.stamp !== selectedRow.lead.updatedAt &&
      !selfSavedUpdatedAtsRef.current.has(selectedRow.lead.updatedAt)
    ) {
      panelKeyRef.current = { ...seen, stamp: selectedRow.lead.updatedAt };
    }
  }
  const panelKeyStamp = panelKeyRef.current?.stamp ?? "";

  // Surfaced from the PAGE, not the panel: an org write can outlive the panel (the
  // operator picks an org and immediately clicks another row), and an error the
  // unmounted panel sets goes nowhere - so the one failure that loses a pick would be
  // the one nobody sees. This banner outlives the panel, and NAMES the lead it is
  // about: a write that fails after the operator has moved on would otherwise read as
  // a failure on the row they just opened.
  // Held with the lead and the decision keys it is about, so a later write only
  // clears it once it has actually redone the write that failed - a tenant pick
  // succeeding says nothing about the landlord write that didn't.
  const [orgSaveError, setOrgSaveError] = useState<{
    leadId: string;
    keys: string[];
    message: string;
  } | null>(null);

  // When the selected lead has a LINKED site (one it created, or an existing
  // platform site it matched), the site's building(s) are the authoritative
  // footprint - the operator may have refined them during site creation - so the
  // right panel shows THOSE on the map + their total area instead of the lead's raw
  // VOA/OSM candidates. Fetch the linked site's buildings and adapt them to the
  // BuildingCandidate shape the panel already renders. `enableNonEssentialQueries:
  // false` keeps this to the plain site GET (no heavy calc/log queries).
  const selectedLinkedSiteId: string | null = selectedRow
    ? (siteIdsByLeadId[selectedRow.lead.id]?.[0] ??
      selectedRow.lead.matchedExistingSiteID ??
      null)
    : null;
  const { fetchSite: linkedSiteFetch } = useSite({
    siteId: selectedLinkedSiteId ?? undefined,
    app: "admin",
    enableNonEssentialQueries: false,
  });
  const linkedSiteData = linkedSiteFetch.data?.site ?? null;
  const siteBuildingCandidates: BuildingCandidate[] | null = useMemo(() => {
    const buildings = linkedSiteData?.buildings ?? [];
    if (!selectedLinkedSiteId || buildings.length === 0) return null;
    return buildings.map((b, i) => {
      // `geometry.coordinates` arrives JSON-stringified on Site buildings;
      // getBuildingCoordinates parses it into the [lon,lat] ring array the
      // candidate pipeline (map render + fit) expects.
      const polygon = getBuildingCoordinates(
        b
      ) as unknown as BuildingCandidate["polygon"];
      const bbox = polygonBbox(polygon);
      const centroidLat = bbox
        ? (bbox[1] + bbox[3]) / 2
        : (linkedSiteData?.latitude ?? 0);
      const centroidLon = bbox
        ? (bbox[0] + bbox[2]) / 2
        : (linkedSiteData?.longitude ?? 0);
      return {
        bkey: b.id || `site-building-${i}`,
        polygon,
        centroidLat,
        centroidLon,
        surfaceAreaSqm: b.surfaceAreaSqM,
        // The site building IS the footprint - not a distance/area-scored
        // guess - so there's no meaningful proximity/area match to compute.
        distanceMetres: 0,
        confidence: 100,
        score: 100,
        proximityWeight: null,
        areaWeight: null,
        areaMatch: null,
      };
    });
  }, [selectedLinkedSiteId, linkedSiteData]);

  // Sort state: an ordered rule list, empty meaning unsorted.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDirection }>(
    DEFAULT_SORT
  );
  // Tag filter is derived from the URL (?tags=hsbc-rfi&tags=...) so a
  // filtered board is shareable/linkable and survives back/forward -
  // same URL-as-source-of-truth approach as `selectedId` above.
  // `setTagFilter` writes back to the URL (replace, to avoid spamming
  // history on every toggle) and preserves the other params.
  const tagFilter = useMemo(
    () => searchParams.getAll("tags").filter(Boolean),
    [searchParams]
  );
  const setTagFilter = (next: string[] | ((prev: string[]) => string[])) => {
    const resolved = typeof next === "function" ? next(tagFilter) : next;
    const params = new URLSearchParams(searchParams);
    params.delete("tags");
    for (const t of resolved) params.append("tags", t);
    setSearchParams(params, { replace: true });
  };

  // Narrow the inbox to one saved search's own rows (?savedSearch=<id>), which
  // is what its promoted count means. Deliberately not the search's tag: a
  // building found by two searches keeps only the first one's tag, so a tag
  // filter would show fewer leads than the count the operator clicked.
  const savedSearchId = searchParams.get("savedSearch");
  const { data: savedSearch, error: savedSearchError } =
    useLeadSearch(savedSearchId);
  const savedSearchRefs: Set<string> | null = useMemo(
    () => (savedSearch ? new Set(savedSearch.sourceRefs) : null),
    [savedSearch]
  );
  const clearSavedSearchFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("savedSearch");
    setSearchParams(params, { replace: true });
  };

  // Multi-select state.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Mid-edit (unsaved) right-panel selections, keyed by leadId. The
  // edit panel calls `onPendingSelectionChange` whenever the
  // operator's working selection changes (without saving); the left-
  // table cells render this overlay so changes show up immediately.
  // Cleared on row-switch / panel-remount via the `key` prop, which
  // re-fires the panel's initial useEffect with the freshly-derived
  // defaults. Mirrors the same pattern on /leads/search so both
  // surfaces behave consistently.
  const [pendingSelectionByLeadId, setPendingSelectionByLeadId] = useState<
    Record<
      string,
      {
        buildingBkey: string | null;
        landlord: SelectedOrg | null;
        tenant: SelectedOrg | null;
        ownerOccupier: boolean;
        dirty: boolean;
      }
    >
  >({});
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const t of r.lead.tags) set.add(t);
    return Array.from(set).sort();
  }, [rows]);

  // Apply the saved-search filter, the tag filter and sort.
  const visible = useMemo(() => {
    let out = rows;
    // Only once the refs have loaded - filtering on an empty set mid-fetch
    // would flash an empty inbox and read as "this search promoted nothing".
    if (savedSearchRefs) {
      out = out.filter(
        (r) => r.lead.source === "voa" && savedSearchRefs.has(r.lead.sourceRef)
      );
    }
    if (tagFilter.length) {
      out = out.filter((r) => tagFilter.every((t) => r.lead.tags.includes(t)));
    }
    {
      const dir = sort.dir === "asc" ? 1 : -1;
      const valueFor = (r: LeadRow, k: SortKey) => {
        switch (k) {
          case "address":
            return (r.lead.address ?? "").toLowerCase();
          case "leadConfidence":
            return r.lead.leadConfidence ?? -1;
          case "locationConfidence":
            return r.locationConfidence ?? -1;
          case "buildingConfidence":
            return r.buildingConfidence ?? -1;
          case "combinedBuildingConfidence":
            return (
              combinedBuildingConfidence(
                r.locationConfidence,
                r.buildingConfidence
              ) ?? -1
            );
          case "surfaceAreaSqm":
            // Sort by the same value the cell shows: a linked site's
            // footprint when present, else the lead's top candidate area.
            return (
              siteBuildingAreaByLeadId[r.lead.id] ??
              r.buildingCandidates[0]?.surfaceAreaSqm ??
              -1
            );
          case "tenantConfidence":
            return r.tenantConfidence ?? -1;
          case "tenantName":
            return (r.tenantName ?? "").toLowerCase();
          case "leadValue":
            return leadValueSortWeight(r.lead.leadValue);
          case "landlordConfidence":
            return r.landlordConfidence ?? -1;
          case "landlordName":
            return (r.landlordName ?? "").toLowerCase();
          case "createdAt":
          default:
            return r.lead.createdAt;
        }
      };
      out = [...out].sort((a, b) => {
        const av = valueFor(a, sort.key);
        const bv = valueFor(b, sort.key);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        // "Value then confidence": value bands are coarse (high / medium / low
        // / unknown), so without this the order inside a band is whatever the
        // list read handed us and shifts between reads. Mirrors the search
        // page's comparator, so the two boards order a value sort alike.
        if (sort.key === "leadValue") {
          const ac = a.lead.leadConfidence ?? -1;
          const bc = b.lead.leadConfidence ?? -1;
          if (ac !== bc) return ac < bc ? -1 * dir : 1 * dir;
        }
        // The final rule under EVERY sort, dropdown option and header click
        // alike. Confidence is a rounded percentage and value is one of five
        // bands, so ties are the common case, and without a last rule the order
        // inside a tie is whatever the list read handed us - which can differ
        // between reads and shuffles rows the operator is working through.
        // Address is the one key on the row that is total and stable. Always
        // ASCENDING, never mirrored by `dir`: a stable ordering, not a second
        // thing ranked on. Skipped when address is itself the sort.
        if (sort.key === "address") return 0;
        const ax = (a.lead.address ?? "").toLowerCase();
        const bx = (b.lead.address ?? "").toLowerCase();
        return ax < bx ? -1 : ax > bx ? 1 : 0;
      });
    }
    return out;
  }, [rows, savedSearchRefs, tagFilter, sort, siteBuildingAreaByLeadId]);

  const pins: OverviewPin[] = useMemo(
    () =>
      visible
        .filter((r) => r.lead.latitude != null && r.lead.longitude != null)
        .map((r) => {
          // Prefer the operator-accepted building polygon; fall back
          // to the top candidate. Mirrors the left-table cell logic
          // so the map shows the same building the row claims.
          const accepted =
            r.buildingCandidates.find(
              (b) => b.bkey === r.initialBuildingBkey
            ) ?? r.buildingCandidates[0];
          return {
            id: r.lead.id,
            lat: r.lead.latitude!,
            lon: r.lead.longitude!,
            label: r.lead.address ?? r.lead.id,
            polygon: accepted?.polygon ?? null,
          };
        }),
    [visible]
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map((r) => r.lead.id)));
  };

  // The panel's `useBlocker` shows the discard prompt when there are
  // unsaved edits and the URL changes; we don't add a parent-side
  // confirm too because that would double-prompt.
  const setSelectedId = (id: string | null) => {
    if (id == null) {
      searchParams.delete("selected");
    } else {
      searchParams.set("selected", id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Every write to a lead's decisions goes through one lane. The Ownership cells now
  // write per pick, so two picks a moment apart - or a pick landing beside an explicit
  // Save - are concurrent PATCHes to the same row, and the persisted company would be
  // whichever the server happened to handle last. Serialised, the operator's last
  // action is the last write.
  const leadWriteChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueueLeadWrite = <T,>(write: () => Promise<T>): Promise<T> => {
    const next = leadWriteChainRef.current.then(write, write);
    // Swallowed on the LANE only, so one failed write doesn't reject every write
    // queued behind it; the returned promise still carries the failure to the caller.
    leadWriteChainRef.current = next.catch(() => {});
    return next;
  };

  const handleSaveOrgDecision = async (patch: OrgDecisionPatch) => {
    if (!selectedRow) return;
    // Only the keys the operator's action actually decided: an omitted key leaves the
    // persisted decision alone (the BE's `_UNSET` sentinel), which is what keeps a
    // tenant pick from writing away a landlord the panel auto-cleared on mount.
    const saved = await updateLeadDecisions.mutateAsync({
      id: selectedRow.lead.id,
      ...(patch.landlord !== undefined && {
        landlordDecision: patch.landlord ? { payload: patch.landlord } : null,
      }),
      ...(patch.tenant !== undefined && {
        tenantDecision: patch.tenant ? { payload: patch.tenant } : null,
      }),
      ...(patch.ownerOccupier !== undefined && {
        ownerOccupier: patch.ownerOccupier,
      }),
    });
    selfSavedUpdatedAtsRef.current.add(saved.lead.updatedAt);
    const written = Object.keys(patch);
    setOrgSaveError((prev) =>
      prev &&
      prev.leadId === selectedRow.lead.id &&
      prev.keys.every((key) => written.includes(key))
        ? null
        : prev
    );
  };

  const saveOrgDecisionReportingErrors = (patch: OrgDecisionPatch) => {
    // Captured now: by the time a failure lands the operator may be on another row.
    const leadId = selectedRow?.lead.id ?? "";
    const address = selectedRow?.lead.address ?? "this lead";
    return enqueueLeadWrite(() => handleSaveOrgDecision(patch)).catch((e) => {
      setOrgSaveError({
        leadId,
        keys: Object.keys(patch),
        message: `Couldn't save the landlord/tenant change on ${address}. It is not saved - reopen that lead and pick it again.`,
      });
      // Re-thrown so the panel does NOT record a failed write as saved (it keys its
      // retry guard on that promise settling).
      throw e;
    });
  };

  // Has-existing-PV once the lead has a linked site. The panel is read-only in
  // that mode and its Save is disabled, but this flag still feeds the outreach
  // email (drafted per lead, not per site), so it gets its own write. Shares
  // `enqueueLeadWrite` with the org-decision writes because both PATCH the same
  // lead row - running them concurrently would let one clobber the other.
  const saveHasPvReportingErrors = (hasPv: boolean) => {
    const leadId = selectedRow?.lead.id ?? "";
    const address = selectedRow?.lead.address ?? "this lead";
    return enqueueLeadWrite(async () => {
      if (!selectedRow) return;
      const saved = await updateLeadDecisions.mutateAsync({
        id: selectedRow.lead.id,
        hasPv,
      });
      selfSavedUpdatedAtsRef.current.add(saved.lead.updatedAt);
      setOrgSaveError((prev) =>
        prev && prev.leadId === leadId && prev.keys.includes("hasPv")
          ? null
          : prev
      );
    }).catch((e) => {
      setOrgSaveError({
        leadId,
        keys: ["hasPv"],
        message: `Couldn't save the existing-solar flag on ${address}. It is not saved - try again.`,
      });
      throw e;
    });
  };

  const handleSave = async (payload: SavePayload) => {
    if (!selectedRow) return;
    const savedLeadId = selectedRow.lead.id;
    await enqueueLeadWrite(() =>
      updateLeadDecisions.mutateAsync({
        id: savedLeadId,
        polygonDecision: payload.buildingBkey
          ? { payload: { acceptedBkey: payload.buildingBkey } }
          : null,
        landlordDecision: payload.landlord
          ? { payload: payload.landlord }
          : null,
        tenantDecision: payload.tenant ? { payload: payload.tenant } : null,
        ownerOccupier: payload.ownerOccupier,
        hasPv: payload.hasPv,
        // Persist an off-list (OSM-clicked) building so its acceptedBkey
        // resolves + site creation can read the geometry.
        newPolygonCandidate: payload.newPolygonCandidate
          ? (payload.newPolygonCandidate as unknown as Record<string, unknown>)
          : undefined,
      })
    );
    // NOT recorded in `selfSavedUpdatedAtsRef`: the explicit Save keeps its remount,
    // which is how the panel drops per-mount state the props can't reset (an off-list
    // OSM building held in `extraCandidates` would otherwise show twice once the save
    // puts it into `buildingCandidates` too). Only the per-pick org writes suppress it.
    // Drop the mid-edit overlay for the saved lead so the left table reflects
    // the just-saved decision from the refetch, instead of holding the
    // "unsaved" pending state until the operator re-selects the row (which
    // is what made Save & Next look like it hadn't updated the left column).
    setPendingSelectionByLeadId((prev) => {
      if (!(savedLeadId in prev)) return prev;
      const next = { ...prev };
      delete next[savedLeadId];
      return next;
    });
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Remove ${selected.size} lead(s) from the list?`)) return;
    await bulkDeleteLeads.mutateAsync({ leadIds: Array.from(selected) });
    if (selectedId && selected.has(selectedId)) setSelectedId(null);
    setSelected(new Set());
  };

  // Panel-level "Remove from leads" handler: deletes the
  // currently-selected lead and clears the panel selection. When
  // the lead is linked to a site, asks separately whether to delete
  // the site too - the answer there determines whether the BE
  // delete-site call also fires before the lead delete.
  const handleRemoveFromLeads = async () => {
    if (!selectedRow) return;
    const linkedSiteId = selectedLinkedSiteId;
    if (linkedSiteId) {
      const deleteSiteToo = confirm(
        "This lead is linked to a site. Delete the site as well?\n\n" +
          "OK = delete the linked site AND remove the lead.\n" +
          "Cancel = leave this dialog and keep both."
      );
      if (!deleteSiteToo) return;
      await deleteSite.mutateAsync(linkedSiteId);
    } else {
      if (!confirm("Remove this lead from the list?")) return;
    }
    await bulkDeleteLeads.mutateAsync({ leadIds: [selectedRow.lead.id] });
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(selectedRow.lead.id);
      return next;
    });
    setSelectedId(null);
  };

  // Panel-level "Create site" handler: reuses the bulk-create flow
  // with a single lead id so the operator gets the same preview +
  // confirm modal they'd see from the toolbar Create N sites button.
  const handleCreateSiteForLead = async () => {
    if (!selectedRow) return;
    await openBulkCreateModal([selectedRow.lead.id]);
  };

  const [helpOpen, setHelpOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState<"add" | "remove" | null>(
    null
  );
  const [tagModalInput, setTagModalInput] = useState("");

  // The create-sites flow, shared with the search / saved-search results pane
  // (see bulkCreateSites.tsx). Getters, not values: `bulkCreateEligibleSelected`
  // below depends on the operation this hook owns.
  const bulkCreate = useBulkCreateSites({
    getLeadsById: () => new Map(rows.map((r) => [r.lead.id, r.lead])),
    getDefaultLeadIds: () => bulkCreateEligibleSelected.map((r) => r.lead.id),
    getIssuesByLeadId: () => bulkCreateIssuesByLeadId,
    onKickedOff: () => setSelected(new Set()),
  });
  const {
    openBulkCreateModal,
    bulkCreateOperation,
    setBulkCreateOperation,
    bulkCreateModalOpen,
    setBulkCreateModalOpen,
    bulkCreateResult,
    bulkCreateSkippedByLeadId,
    operationStatus,
    operationProgress,
    previewIsLoading: previewBulkCreateSitesIsLoading,
  } = bulkCreate;
  // Set of lead IDs that are currently being turned into sites by the
  // active bulk-create operation. Drives:
  // - Per-row "Creating site..." indicator in the Site column.
  // - Exclusion from `bulkCreateEligibleSelected` so the operator can't
  //   accidentally kick a second create for the same leads.
  // - Disabling the "Create N sites" button while an operation is in
  //   flight (concurrent ops would need multi-op polling support).
  const pendingCreateLeadIds = useMemo(
    () => new Set(bulkCreateOperation?.leadIds ?? []),
    [bulkCreateOperation]
  );
  // Larger selections show an error before the call goes out so we
  // don't burn a 400 round-trip.
  const LEAD_CONFIDENCE_PREMIUM_GATE = 80;
  // Bulk-create eligibility: selected leads with no linked site yet.
  // A lead is "linked" if it either originated a site (visible via
  // `siteIdsByLeadId` from the /leads response) or matches an
  // existing platform site (`matchedExistingSiteID`). Defined ahead
  // of the confidence selectors so they can scope themselves to
  // eligible rows - we don't want the modal to warn about a 70%
  // landlord match for a lead that already has a site and won't
  // be processed anyway.
  const bulkCreateEligibleSelected = useMemo(() => {
    return rows.filter((r) => {
      if (!selected.has(r.lead.id)) return false;
      if (r.lead.matchedExistingSiteID) return false;
      if ((siteIdsByLeadId[r.lead.id] ?? []).length > 0) return false;
      // A site creation is already in flight for this lead - don't
      // let the operator kick off a second one. Falls back into the
      // eligible set automatically once the in-flight op clears.
      if (pendingCreateLeadIds.has(r.lead.id)) return false;
      // No polygon -> can't create a site. The BE would skip these
      // anyway; pre-filter so the count + preview reflect what's
      // actually going to happen.
      if (r.buildingCandidates.length === 0) return false;
      return true;
    });
  }, [rows, selected, siteIdsByLeadId, pendingCreateLeadIds]);

  // Selected rows that can't be created because they have no
  // building polygon. Surfaced in the modal so the operator
  // understands the "Create N sites" count drops below the
  // selected-row count.
  const noPolygonSelected = useMemo(() => {
    return rows.filter((r) => {
      if (!selected.has(r.lead.id)) return false;
      if (r.lead.matchedExistingSiteID) return false;
      if ((siteIdsByLeadId[r.lead.id] ?? []).length > 0) return false;
      return r.buildingCandidates.length === 0;
    });
  }, [rows, selected, siteIdsByLeadId]);
  const eligibleIdSet = useMemo(
    () => new Set(bulkCreateEligibleSelected.map((r) => r.lead.id)),
    [bulkCreateEligibleSelected]
  );
  const lowConfidenceSelected = useMemo(() => {
    return rows.filter(
      (r) =>
        eligibleIdSet.has(r.lead.id) &&
        r.lead.leadConfidence !== null &&
        r.lead.leadConfidence < LEAD_CONFIDENCE_PREMIUM_GATE
    );
  }, [rows, eligibleIdSet]);
  // Per-row breakdown of *which* signals are below the 80% gate, so
  // the operator knows whether premium enrichment is likely to help
  // (missing landlord/tenant) or whether the issue is location/
  // building polygon (which premium can't fix). The "premium may
  // help" hint flips to "premium ran with no result" once
  // `premiumEnrichedAt` is set - otherwise the message tells the
  // operator to do something they've already done.
  const issuesForRow = (r: LeadRow): string[] => {
    const issues: string[] = [];
    const premiumRan = r.premiumEnrichedAt != null;
    const noMatchHint = premiumRan
      ? "premium enrichment ran with no result"
      : "premium enrichment may help";
    if (!r.lead.landlordDecision) {
      issues.push(`no landlord match found - ${noMatchHint}`);
    } else if (
      r.landlordConfidence != null &&
      r.landlordConfidence < LEAD_CONFIDENCE_PREMIUM_GATE
    ) {
      const name = r.landlordName ?? "selected landlord";
      issues.push(
        `landlord match low (${name} at ${Math.round(r.landlordConfidence)}%)`
      );
    }
    if (!r.lead.ownerOccupier) {
      if (!r.lead.tenantDecision) {
        issues.push(`no tenant match found - ${noMatchHint}`);
      } else if (
        r.tenantConfidence != null &&
        r.tenantConfidence < LEAD_CONFIDENCE_PREMIUM_GATE
      ) {
        const name = r.tenantName ?? "selected tenant";
        issues.push(
          `tenant match low (${name} at ${Math.round(r.tenantConfidence)}%)`
        );
      }
    }
    const buildingConf = r.buildingCandidates[0]?.confidence;
    if (buildingConf != null && buildingConf < LEAD_CONFIDENCE_PREMIUM_GATE) {
      issues.push(`building polygon match low (${Math.round(buildingConf)}%)`);
    }
    if (
      r.locationConfidence != null &&
      r.locationConfidence < LEAD_CONFIDENCE_PREMIUM_GATE
    ) {
      issues.push(
        `geocode confidence low (${Math.round(r.locationConfidence)}%)`
      );
    }
    return issues;
  };
  const incompleteSelected = useMemo(() => {
    return rows.filter(
      (r) => eligibleIdSet.has(r.lead.id) && r.lead.leadConfidence === null
    );
  }, [rows, eligibleIdSet]);
  // Per-lead issue text shown inline in the bulk-create per-site
  // table (one bullet per row, under the address). Only rows below
  // the premium-enrich gate get issues; clean rows get an empty
  // array which the table renders as nothing.
  const bulkCreateIssuesByLeadId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of lowConfidenceSelected) {
      const issues = issuesForRow(r);
      if (issues.length > 0) map.set(r.lead.id, issues);
    }
    return map;
    // issuesForRow is a stable function defined in the same scope -
    // re-deriving when the inputs change is enough. eslint-disable-next-line
    // react-hooks/exhaustive-deps
  }, [lowConfidenceSelected]);

  // Premium-enrich eligibility: VOA-sourced rows that haven't already
  // been premium-enriched. Mirrors `premiumEligible` on /leads/search.
  // The freshness flag lives on `tenantData.premiumEnrichedAt` (set
  // by `_sync_lead_data_from_voa` after the BE runs the paid passes).
  const premiumEligibleSelected = useMemo(() => {
    return rows.filter((r) => {
      if (!selected.has(r.lead.id)) return false;
      if (r.lead.source !== "voa") return false;
      const tenantData =
        (r.lead.tenantData as Record<string, unknown> | null) ?? {};
      return tenantData.premiumEnrichedAt == null;
    });
  }, [rows, selected]);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [premiumError, setPremiumError] = useState<string | null>(null);
  // BE hard cap; matches `_BULK_PREMIUM_ENRICH_MAX_PER_OPERATION`.
  // Each lead runs the paid Google passes (~1-2s) plus the rate-
  // limited CH lookup; 50 keeps the worst case well inside the
  // 15-min Lambda budget.
  const BULK_PREMIUM_ENRICH_MAX_PER_OPERATION = 50;
  // The in-flight premium-enrich operation we're polling. Cleared when
  // the modal is closed. The BE used to run this synchronously but
  // batches of 20+ leads blew past API Gateway's 29s ceiling - the FE
  // now kicks off an LRO and polls `/operations/<key>` for completion.
  const [premiumOperation, setPremiumOperation] = useState<{
    operationKey: string;
    siteId: string;
    leadCount: number;
  } | null>(null);
  const handleConfirmPremium = async () => {
    if (premiumEligibleSelected.length === 0) {
      setPremiumModalOpen(false);
      return;
    }
    setPremiumError(null);
    try {
      const response = await bulkPremiumEnrichLeads.mutateAsync({
        leadIds: premiumEligibleSelected.map((r) => r.lead.id),
      });
      setPremiumOperation({
        operationKey: response.operationKey,
        siteId: response.siteId,
        leadCount: response.leadCount,
      });
    } catch (err) {
      setPremiumError((err as Error)?.message ?? "Premium enrichment failed.");
    }
  };
  const closePremiumModal = () => {
    setPremiumModalOpen(false);
    setPremiumOperation(null);
    setPremiumError(null);
  };
  const premiumOperationPoll = useOperation(
    premiumOperation?.operationKey ?? null,
    premiumOperation?.siteId ?? null
  );
  const premiumOperationStatus =
    premiumOperationPoll.data?.operation?.status ?? null;
  const premiumOperationProgress =
    premiumOperationPoll.data?.operation?.progress ?? null;
  const premiumOperationResult =
    premiumOperationStatus === "completed" &&
    premiumOperationPoll.data?.operation?.result
      ? (premiumOperationPoll.data.operation
          .result as unknown as LeadsBulkPremiumEnrichOperationResult)
      : null;
  // Once the LRO finishes, invalidate the leads cache so the table
  // picks up the refreshed enrichment data. Mirrors the bulk-create
  // pattern - the mutation's own onSuccess only fires when the
  // operation is *queued*, not when the BE worker completes.
  useEffect(() => {
    if (premiumOperationStatus === "completed") {
      queryClient.invalidateQueries(["leads"]);
      if (premiumOperationResult) {
        for (const lead of premiumOperationResult.leads) {
          queryClient.invalidateQueries(["lead", lead.id]);
        }
      }
    }
  }, [premiumOperationStatus, premiumOperationResult, queryClient]);
  // Poll the landlord-research LRO; on completion capture the trace run id,
  // surface a not_configured/failed message, and invalidate the lead so its
  // freshly-written agenticData renders in the panel. Mirrors LeadSearchPage.
  const researchOpPoll = useOperation(
    researchOp?.operationKey ?? null,
    researchOp?.siteId ?? null
  );
  useEffect(() => {
    if (!researchOp) return;
    const op = researchOpPoll.data?.operation;
    if (!op) return;
    if (op.status === "completed") {
      // The trace run_id is persisted onto the lead's agenticData by the worker;
      // the ["lead", id] invalidation below refetches it, so the panel reads it off
      // the row rather than a session-scoped map.
      const rawResultStatus = op.result?.status;
      const resultStatus =
        typeof rawResultStatus === "string" ? rawResultStatus : null;
      if (resultStatus === "not_configured") {
        setResearchError(
          "Web search isn't configured for this environment yet - the research gateway needs provisioning before landlord research can run."
        );
      }
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", researchOp.leadId]);
      setResearchOp(null);
    } else if (op.status === "failed" || op.status === "cancelled") {
      setResearchError("Landlord research failed - see the operations page.");
      setResearchOp(null);
    }
    // Also key on the attached op's identity so that when a re-hydrated op
    // (navigated away and back, so `researchOp` re-loaded from sessionStorage)
    // re-attaches to a poll whose first resolved status is already terminal,
    // the completion refetch + clear still fire (status alone would not
    // "transition" on re-attach). eslint-disable: researchOpPoll/queryClient
    // are intentionally excluded to avoid re-running on every poll tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [researchOpPoll.data?.operation?.status, researchOp?.operationKey]);

  // Bulk-research poll: on completion, invalidate the leads cache so every
  // researched lead's agenticData shows, and surface an actionable tail (rows
  // beyond the per-run cap / failures). A clean run just refreshes silently.
  const bulkResearchOpPoll = useOperation(
    bulkResearchOp?.operationKey ?? null,
    bulkResearchOp?.siteId ?? null
  );
  // Incremental reveal: the bulk worker researches leads one at a time and bumps
  // progress.current as each finishes. Refresh the list + the just-finished lead each
  // time it advances, so a finished lead's researched landlord appears mid-batch instead
  // of every lead staying "researching" until the whole batch ends.
  const lastBulkResearchCurrent = useRef<number>(0);
  // Total hard failures across a whole auto-continue chain, so the final message
  // reports every wave's failures, not just the last. Reset on a fresh chain
  // (manual launch or "run again"), accumulated on each auto-continue hop.
  const chainFailedResearchCount = useRef<number>(0);
  useEffect(() => {
    if (!bulkResearchOp) return;
    const op = bulkResearchOpPoll.data?.operation;
    if (!op) return;
    const current = op.progress?.current ?? 0;
    if (
      op.status !== "completed" &&
      current > lastBulkResearchCurrent.current
    ) {
      lastBulkResearchCurrent.current = current;
      queryClient.invalidateQueries(["leads"]);
      const justFinished = bulkResearchOp.leadIds[current - 1];
      if (justFinished) queryClient.invalidateQueries(["lead", justFinished]);
    }
    if (op.status === "completed") {
      const result = op.result as BulkResearchResultShape | undefined;
      const researchedLeadIds = bulkResearchOp.leadIds;
      const inputIds = bulkResearchOp.inputIds;
      const cancelled = bulkResearchOp.cancelledRefs;
      setBulkResearchOp(null);
      lastBulkResearchCurrent.current = 0;
      if (result?.status === "not_configured") {
        chainFailedResearchCount.current = 0;
        setResearchError(
          "Web search isn't configured for this environment yet - landlord research can't run."
        );
      } else {
        // Shared decision (see planBulkResearchContinuation): what's left is
        // derived from what actually got done, so leads past the BE per-batch cap
        // aren't silently dropped; the chain auto-continues until none remain.
        const plan = planBulkResearchContinuation(
          result ?? {},
          inputIds,
          cancelled
        );
        if (plan.action === "continue") {
          // Carry this wave's failures forward so the chain's final message
          // reports the total, not just the last wave's.
          chainFailedResearchCount.current += plan.batchFailedCount;
          void launchBulkResearchLeads(plan.remaining);
        } else {
          const totalFailed =
            chainFailedResearchCount.current + plan.batchFailedCount;
          chainFailedResearchCount.current = 0;
          if (plan.remaining.length > 0) {
            setResearchNotRunRefs((prev) => {
              const next = new Set(prev);
              plan.remaining.forEach((id) => next.add(id));
              return next;
            });
          }
          if (plan.remaining.length > 0 || totalFailed > 0) {
            setResearchError(
              (plan.remaining.length > 0
                ? `${plan.remaining.length} lead${plan.remaining.length === 1 ? "" : "s"} could not be researched - use "run again" on those rows.`
                : "") +
                (totalFailed > 0
                  ? `${plan.remaining.length > 0 ? " " : ""}${totalFailed} failed - see the operations page.`
                  : "")
            );
          }
        }
      }
      queryClient.invalidateQueries(["leads"]);
      // Mirror the single-row path: the per-lead fetch (`["lead", id]`) feeds the
      // open panel's Contacts section, which reads the (now possibly auto-picked)
      // landlordDecision - so refresh each researched lead, not just the list.
      researchedLeadIds.forEach((id) =>
        queryClient.invalidateQueries(["lead", id])
      );
    } else if (op.status === "failed" || op.status === "cancelled") {
      setResearchError(
        "Bulk landlord research failed - see the operations page."
      );
      setBulkResearchOp(null);
      lastBulkResearchCurrent.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bulkResearchOpPoll.data?.operation?.status,
    bulkResearchOpPoll.data?.operation?.progress?.current,
  ]);

  // Read once for the render: the per-row research chips + the right panel derive each
  // lead's done / researching / queued state from this single bulk-op poll.
  const bulkResearchProgress = bulkResearchOpPoll.data?.operation?.progress;
  const bulkResearchCurrent = bulkResearchProgress?.current ?? 0;
  const bulkResearchActiveSearches = bulkResearchProgress?.processed ?? 0;
  const bulkResearchCancelledRefs = bulkResearchOp?.cancelledRefs ?? null;
  // The current sub-batch's overflow: leads selected into the chain that this
  // batch didn't queue and that auto-continue will pick up next (shown "queued").
  const bulkResearchChainPendingRefs = useMemo(() => {
    if (!bulkResearchOp) return null;
    const queued = new Set(bulkResearchOp.leadIds);
    return new Set(bulkResearchOp.inputIds.filter((id) => !queued.has(id)));
  }, [bulkResearchOp]);
  // The open lead's research state (done / researching / queued / cancelled / null),
  // so the panel reads "Queued" vs "Researching" and offers Cancel only while it's
  // still queued.
  //
  // Must go through researchRowChipState - the SAME wrapper the list cell uses, as
  // /leads/search already does - not the bare bulkResearchRowStateForRef underneath
  // it. The bare helper sees only this page's local handles, so a run this tab isn't
  // tracking (a second single-row launch, another tab, a reload) left the panel
  // offering "Research landlord" on a row the list was still showing as researching.
  const selectedResearchState = selectedRow
    ? researchRowChipState({
        ref: selectedRow.lead.id,
        singleResearchingRefs,
        bulkRefs: bulkResearchOp?.leadIds,
        bulkCurrent: bulkResearchCurrent,
        bulkCancelledRefs: bulkResearchCancelledRefs,
        chainPendingRefs: bulkResearchChainPendingRefs,
        serverOperationKey: selectedRow.lead.researchOperationKey,
      })
    : null;

  // The live research run covering the OPEN row, whoever launched it: the row's own
  // key, or - for the moment between this tab's click and the key coming back - the
  // handle from that launch. Polling the open row (rather than whichever run this tab
  // launched most recently) is what lets the panel show a live "(N searches run)" for
  // every researching row, not just the last one clicked.
  const selectedResearchOperationKey = selectedRow
    ? (selectedRow.lead.researchOperationKey ??
      (researchOp?.leadId === selectedRow.lead.id
        ? researchOp.operationKey
        : null))
    : null;
  // Same query key as any other poll of this operation, so when it IS the run this tab
  // launched the two share one request rather than doubling it.
  const selectedResearchPoll = useOperation(
    selectedResearchOperationKey,
    selectedResearchOperationKey ? LEADS_RESEARCH_SITE_ID : null
  );
  const selectedResearchOperation = selectedResearchPoll.data?.operation;
  // Only a single-subject run's count belongs to this row (see the type constant).
  const selectedResearchSearchCount =
    selectedResearchOperation?.operationType === SINGLE_RESEARCH_OPERATION_TYPE
      ? (selectedResearchOperation.progress?.processed ?? null)
      : null;

  // Launch one bulk-research sub-batch for `leadIds` (no confirm). Used by the
  // toolbar action (after its confirm), by auto-continue (the completion effect
  // re-launches with the leftover), and by a row's "run again". The BE caps the
  // batch and returns queuedRefs; the overflow folds into the auto-continue
  // chain. The caller guards against launching over an in-flight op.
  const launchBulkResearchLeads = async (leadIds: string[]) => {
    if (leadIds.length === 0) return;
    // These leads are being (re-)researched, so drop any stale "not run" markers.
    setResearchNotRunRefs((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      leadIds.forEach((id) => next.delete(id));
      return next;
    });
    setResearchError(null);
    try {
      const res = await bulkResearchLeadLandlords.mutateAsync(leadIds);
      if (res.status === "noop") {
        setResearchError(
          "None of the selected leads are available to research."
        );
        return;
      }
      if (res.operationKey && res.siteId) {
        setBulkResearchOp({
          operationKey: res.operationKey,
          siteId: res.siteId,
          // Only the leads the BE actually queued (capped/eligible-filtered) are
          // shown "researching"; the overflow leads fold into the chain (shown
          // "queued"). Fall back to the full input if the BE is older.
          leadIds: res.queuedRefs ?? leadIds,
          inputIds: leadIds,
          cancelledRefs: new Set(),
        });
      }
    } catch {
      setResearchError("Could not start bulk landlord research. Try again.");
    }
  };
  const handleBulkResearchLeads = async () => {
    if (selected.size === 0 || bulkResearchOp !== null) {
      return;
    }
    const leadIds = Array.from(selected);
    const confirmed = window.confirm(
      `Research the landlord for ${leadIds.length} selected lead${
        leadIds.length === 1 ? "" : "s"
      }?\n\nThis runs the paid agentic research (up to ~$2 each, so up to ~$${(
        leadIds.length * 2
      ).toFixed(
        2
      )} total) and can take a few minutes. Leads that already have a landlord are re-researched (and re-billed). The batch researches in waves and continues automatically until every selected lead is done.`
    );
    if (!confirmed) {
      return;
    }
    chainFailedResearchCount.current = 0;
    await launchBulkResearchLeads(leadIds);
  };
  // "Run again" on a lead a finished chain never reached: re-launch a one-lead
  // chain for it (bounded, mirrors the toolbar launch path). Guarded against an
  // in-flight op so it can't race a running chain.
  const handleRerunResearch = (leadId: string) => {
    if (bulkResearchOp !== null) return;
    chainFailedResearchCount.current = 0;
    void launchBulkResearchLeads([leadId]);
  };
  // Pull one still-queued lead out of the running batch, keeping the rest. Optimistic:
  // mark it cancelled locally so the row + panel button drop "queued" at once, then tell
  // the BE (the worker skips it before its turn). On failure, roll the local mark back
  // so the lead rejoins the visible queue and surface the error.
  const handleCancelQueuedResearch = async (leadId: string) => {
    const op = bulkResearchOp;
    if (!op) return;
    setBulkResearchOp((prev) =>
      prev
        ? { ...prev, cancelledRefs: new Set(prev.cancelledRefs).add(leadId) }
        : prev
    );
    try {
      await cancelResearchRow.mutateAsync({
        operationKey: op.operationKey,
        sourceRef: leadId,
      });
    } catch {
      setBulkResearchOp((prev) => {
        if (!prev) return prev;
        const next = new Set(prev.cancelledRefs);
        next.delete(leadId);
        return { ...prev, cancelledRefs: next };
      });
      setResearchError("Could not cancel that queued lead. Try again.");
    }
  };
  // Top tag chips shown above the Add tag input - lets the operator
  // re-use existing tags with one click instead of re-typing. First
  // chip is the most-recently-used tag (even if not the most
  // popular) because operators tend to work through a single batch
  // session and the tag they just applied is the most likely to be
  // wanted again. Mirrors the same logic on /leads/search's
  // Promote modal.
  const topTagChips = useMemo<string[]>(() => {
    const allLeads = fetchLeads.data?.leads ?? [];
    if (allLeads.length === 0) return [];
    const counts = new Map<string, number>();
    const lastUsed = new Map<string, number>();
    for (const lead of allLeads) {
      const tags = lead.tags ?? [];
      const ts = lead.createdAt ? new Date(lead.createdAt).getTime() : 0;
      for (const t of tags) {
        counts.set(t, (counts.get(t) ?? 0) + 1);
        const prev = lastUsed.get(t) ?? 0;
        if (ts > prev) lastUsed.set(t, ts);
      }
    }
    if (counts.size === 0) return [];
    const allTags = [...counts.keys()];
    let mostRecent: string | null = null;
    let mostRecentTs = -Infinity;
    for (const t of allTags) {
      const ts = lastUsed.get(t) ?? 0;
      if (ts > mostRecentTs) {
        mostRecentTs = ts;
        mostRecent = t;
      }
    }
    const byCountDesc = allTags
      .filter((t) => t !== mostRecent)
      .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0));
    return [mostRecent, ...byCountDesc]
      .filter((t): t is string => t != null)
      .slice(0, 5);
  }, [fetchLeads.data]);

  const addTagChipToInput = (tag: string) => {
    setTagModalInput((prev) => {
      const existing = prev
        .split("\n")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (existing.includes(tag.toLowerCase())) return prev;
      return prev.trim() ? `${prev.replace(/\s+$/, "")}\n${tag}` : tag;
    });
  };

  // Selected tags-to-remove for the bulk Remove tag modal. Driven by
  // chip clicks instead of free-text input - operators were having
  // to retype tags exactly, and a typo silently no-op'd the BE call.
  const [tagsToRemove, setTagsToRemove] = useState<Set<string>>(new Set());

  // Union of tags actually present on the currently-selected leads.
  // Used to populate the chip list in the Remove tag modal.
  const tagsOnSelectedLeads = useMemo<string[]>(() => {
    if (selected.size === 0) return [];
    const set = new Set<string>();
    for (const r of rows) {
      if (!selected.has(r.lead.id)) continue;
      for (const t of r.lead.tags ?? []) set.add(t);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows, selected]);

  const handleBulkTags = async () => {
    if (!tagModalOpen || selected.size === 0) return;
    if (tagModalOpen === "add") {
      const tags = tagModalInput
        .split("\n")
        .map((t) => t.trim())
        .filter(Boolean);
      if (!tags.length) return;
      await bulkUpdateLeadTags.mutateAsync({
        leadIds: Array.from(selected),
        addTags: tags,
      });
    } else {
      const tags = [...tagsToRemove];
      if (!tags.length) return;
      await bulkUpdateLeadTags.mutateAsync({
        leadIds: Array.from(selected),
        removeTags: tags,
      });
    }
    setTagModalOpen(null);
    setTagModalInput("");
    setTagsToRemove(new Set());
  };

  // Auto-clear selection if it falls out of the visible filter (e.g. a
  // tag filter now excludes the selected lead). Guard on the leads query
  // having loaded: on a hard refresh `rows` (= fetchLeads.data) is
  // momentarily empty, so without this guard the effect fires before the
  // leads arrive and drops a `?selected=<id>` deep link (landing the
  // operator back on the bare list). Once loaded, a genuinely
  // filtered-out or deleted selection still clears.
  useEffect(() => {
    if (fetchLeads.isLoading) return;
    if (selectedId && !visible.find((r) => r.lead.id === selectedId)) {
      setSelectedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.length, fetchLeads.isLoading]);

  // Below ~1024px the 55/45 two-panel layout is too cramped; show
  // only the primary panel - list when nothing's selected, detail
  // when something is.
  const isCompact = useIsCompactLayout();
  const showLeft = !isCompact || !selectedRow;
  const showRight = !isCompact || !!selectedRow;

  return (
    <Container
      fluid
      style={{
        padding: 0,
        flex: 1,
        minHeight: 0,
        // A column, so the banner below takes its own height and the panels row takes
        // what is left. As a plain block it would sit above a `height: 100%` row and
        // push the layout over by exactly the banner's height.
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Above BOTH panels deliberately: an org write can outlive the panel that
          started it (pick, then immediately deselect the row), and in the compact
          layout deselecting unmounts the whole right-hand column - so a banner
          rendered in either would go down with it, taking the only notice that a
          pick was lost. */}
      {orgSaveError && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setOrgSaveError(null)}
          style={{ fontSize: 13, margin: 8 }}
        >
          {orgSaveError.message}
        </Alert>
      )}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {showLeft && (
          <div
            style={{
              width: isCompact ? "100%" : "55%",
              borderRight: isCompact ? "none" : "1px solid #ddd",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "8px 12px",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <h5 style={{ margin: 0 }}>Leads ({visible.length})</h5>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate("/leads/search")}
              >
                New search
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate("/leads/search/history")}
              >
                Saved searches
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate("/leads/import")}
              >
                Bulk load leads
              </Button>
              <Button
                size="sm"
                variant="link"
                style={{ marginLeft: "auto", padding: 0, fontSize: 14 }}
                onClick={() => setHelpOpen(true)}
              >
                How does this work?
              </Button>
            </div>
            {bulkCreateOperation && !bulkCreateModalOpen && (
              <Alert
                variant={
                  operationStatus === "completed"
                    ? "success"
                    : operationStatus === "failed" ||
                        operationStatus === "cancelled"
                      ? "danger"
                      : "info"
                }
                style={{
                  margin: "8px 12px",
                  padding: "6px 12px",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span style={{ flex: 1 }}>
                  {operationStatus === "completed" ? (
                    (() => {
                      const created =
                        bulkCreateResult?.createdSites.length ?? 0;
                      const skipped = bulkCreateResult?.skipped.length ?? 0;
                      if (skipped > 0) {
                        return (
                          <>
                            <strong>
                              Created {created} site{created === 1 ? "" : "s"};{" "}
                              {skipped} skipped.
                            </strong>{" "}
                            Skipped reasons are shown in the Site column on each
                            row - hover for details, or click View progress for
                            the full breakdown.
                          </>
                        );
                      }
                      return (
                        <>
                          <strong>
                            Created {created} site{created === 1 ? "" : "s"}
                          </strong>{" "}
                          from selected leads. The links are now live in the
                          Site column.
                        </>
                      );
                    })()
                  ) : operationStatus === "failed" ||
                    operationStatus === "cancelled" ? (
                    <>
                      <strong>Bulk-create failed.</strong> Open the modal to see
                      the per-lead error breakdown.
                    </>
                  ) : (
                    <>
                      <strong>
                        Creating {bulkCreateOperation.leadCount} site
                        {bulkCreateOperation.leadCount === 1 ? "" : "s"}...
                      </strong>{" "}
                      {operationProgress?.message ?? "Starting up..."}
                      {operationProgress?.processed != null &&
                        operationProgress?.total != null &&
                        ` (${operationProgress.processed}/${operationProgress.total})`}
                    </>
                  )}
                </span>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => setBulkCreateModalOpen(true)}
                  style={{ padding: 0, fontSize: 12 }}
                >
                  View progress
                </Button>
                {(operationStatus === "completed" ||
                  operationStatus === "failed" ||
                  operationStatus === "cancelled") && (
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => setBulkCreateOperation(null)}
                    style={{
                      padding: 0,
                      fontSize: 12,
                      color: "#6c757d",
                    }}
                  >
                    Dismiss
                  </Button>
                )}
              </Alert>
            )}
            {savedSearchId != null && (
              <div
                style={{
                  padding: "6px 12px",
                  borderBottom: "1px solid #eee",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <span>Showing leads from saved search:</span>
                <Badge
                  bg="primary"
                  style={{ fontWeight: 400, fontSize: 13 }}
                  title="Leads promoted from this search's rows, whoever promoted them"
                >
                  {savedSearchError != null
                    ? "could not load this search"
                    : (savedSearch?.name ?? "loading...")}
                </Badge>
                <Button
                  size="sm"
                  variant="link"
                  style={{ padding: 0, fontSize: 13 }}
                  onClick={clearSavedSearchFilter}
                >
                  clear
                </Button>
              </div>
            )}
            <div
              style={{
                padding: "6px 12px",
                borderBottom: "1px solid #eee",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              <SortBySelect
                options={SORT_OPTIONS}
                sortKey={sort.key}
                sortDir={sort.dir}
                onChange={(key, dir) => setSort({ key, dir })}
                ariaLabel="Sort leads by"
              />
              {allTags.length > 0 && (
                <>
                  <span
                    style={{ color: "#666", marginLeft: 8, marginRight: 4 }}
                  >
                    Filter by tag:
                  </span>
                  {allTags.map((t) => {
                    const on = tagFilter.includes(t);
                    return (
                      <Badge
                        key={t}
                        bg={on ? "primary" : "light"}
                        text={on ? "light" : "dark"}
                        style={{
                          cursor: "pointer",
                          fontWeight: 400,
                          border: "1px solid #ccc",
                          fontSize: 13,
                        }}
                        onClick={() =>
                          setTagFilter((prev) =>
                            on ? prev.filter((x) => x !== t) : [...prev, t]
                          )
                        }
                      >
                        {t}
                      </Badge>
                    );
                  })}
                  {tagFilter.length > 0 && (
                    <Button
                      size="sm"
                      variant="link"
                      style={{ padding: 0, fontSize: 13 }}
                      onClick={() => setTagFilter([])}
                    >
                      clear
                    </Button>
                  )}
                  {tagFilter.length === 1 && (
                    <Button
                      size="sm"
                      variant="link"
                      style={{ padding: 0, fontSize: 13, marginLeft: 12 }}
                      onClick={() =>
                        navigate(
                          `/leads/portfolio/${encodeURIComponent(tagFilter[0])}`
                        )
                      }
                    >
                      Review portfolio
                    </Button>
                  )}
                </>
              )}
            </div>
            {selected.size > 0 && (
              <div
                style={{
                  padding: "6px 12px",
                  background: "#fff3cd",
                  borderBottom: "1px solid #ffe69c",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 12 }}>{selected.size} selected:</span>
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={() => openBulkCreateModal()}
                  disabled={
                    bulkCreateEligibleSelected.length === 0 ||
                    previewBulkCreateSitesIsLoading ||
                    // One bulk-create LRO at a time - the FE only
                    // polls one operation key. Concurrent ops would
                    // need a multi-op polling refactor; for now the
                    // operator waits for the current op to finish
                    // (or dismisses it via the banner).
                    (bulkCreateOperation != null &&
                      operationStatus !== "completed" &&
                      operationStatus !== "failed" &&
                      operationStatus !== "cancelled")
                  }
                  title={
                    bulkCreateOperation != null &&
                    operationStatus !== "completed" &&
                    operationStatus !== "failed" &&
                    operationStatus !== "cancelled"
                      ? "A bulk-create operation is currently running. Wait for it to finish (or dismiss the banner) before starting a new batch."
                      : "Create sites from the selected leads, deduplicating tenant + landlord orgs in one step. Leads that already have a linked site are excluded."
                  }
                >
                  Create {bulkCreateEligibleSelected.length} site
                  {bulkCreateEligibleSelected.length === 1 ? "" : "s"}
                </Button>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setPremiumError(null);
                    setPremiumModalOpen(true);
                  }}
                  disabled={
                    premiumEligibleSelected.length === 0 ||
                    bulkPremiumEnrichLeads.isLoading
                  }
                  title="Run paid Google Places APIs against the selected leads. Skips leads that already have premium data."
                >
                  {bulkPremiumEnrichLeads.isLoading
                    ? "Enriching..."
                    : `Enrich ${premiumEligibleSelected.length} ($${(
                        premiumEligibleSelected.length * 0.01
                      ).toFixed(2)})`}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleBulkResearchLeads}
                  disabled={
                    selected.size === 0 ||
                    bulkResearchOp !== null ||
                    bulkResearchLeadLandlords.isLoading
                  }
                  title="Run the paid agentic landlord research (up to ~$2/lead) on every selected lead. Already-researched leads are re-researched and re-billed. A limited number run per batch - re-run for the rest."
                >
                  {bulkResearchOp
                    ? (() => {
                        const p = bulkResearchOpPoll.data?.operation?.progress;
                        return p
                          ? `Researching landlords ${p.current}/${p.total}...`
                          : "Researching landlords...";
                      })()
                    : `Research landlord (${selected.size})`}
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={handleDelete}
                >
                  Remove
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTagModalOpen("add")}
                >
                  Add tag
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTagModalOpen("remove")}
                >
                  Remove tag
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => setSelected(new Set())}
                >
                  Clear selection
                </Button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {/*
              Set the default cell font-size on the table so unstyled
              cells (Tenant, Landlord, Lead, Tags) inherit a consistent
              13.5px. Bumped +1.5 from 12 for readability - the dense
              12px table was hard to read.
            */}
              <Table
                size="sm"
                hover
                style={{ marginBottom: 0, fontSize: 13.5 }}
              >
                <thead
                  style={{ position: "sticky", top: 0, background: "#fff" }}
                >
                  <tr>
                    <th style={{ width: 28 }}>
                      <Form.Check
                        checked={
                          selected.size > 0 && selected.size === visible.length
                        }
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                    <SortHeader
                      label="Value"
                      sortKey="leadValue"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Conf"
                      sortKey="leadConfidence"
                      sort={sort}
                      setSort={setSort}
                      tooltip="Overall lead confidence (min of building, landlord, tenant). Missing roles are skipped, not zeroed - mirrors the same score the candidate had on /leads/search. < 80% prompts premium enrichment on site creation."
                    />
                    <SortHeader
                      label="Address"
                      sortKey="address"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Building (m²)"
                      sortKey="surfaceAreaSqm"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Tnt"
                      sortKey="tenantConfidence"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Tenant"
                      sortKey="tenantName"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Lnd"
                      sortKey="landlordConfidence"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Landlord"
                      sortKey="landlordName"
                      sort={sort}
                      setSort={setSort}
                    />
                    <th
                      title="Contacts we have drafted an outreach email for. Reruns of the same email are not counted."
                      style={{ textAlign: "right" }}
                    >
                      Emails
                    </th>
                    <th>Tags</th>
                    <th>Site</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const isSelected = selected.has(r.lead.id);
                    const isFocused = r.lead.id === selectedId;
                    // Pending right-panel selection for this row (if
                    // any). Lets the left-table cells render mid-edit
                    // state immediately - same pattern as
                    // /leads/search. When pending is absent, fall back
                    // to the row's pre-computed initial display
                    // (saved decision -> top default).
                    const pending = pendingSelectionByLeadId[r.lead.id];
                    // Only respect pending state when the operator has
                    // actually changed something - otherwise we'd flag
                    // the row "unsaved" the moment they opened it (the
                    // panel emits its defaulted-but-not-changed state
                    // on mount).
                    const pendingDirty = pending?.dirty ? pending : null;
                    const effectiveBuildingBkey =
                      pendingDirty?.buildingBkey ?? r.initialBuildingBkey;
                    const effectiveBuilding =
                      r.buildingCandidates.find(
                        (b) => b.bkey === effectiveBuildingBkey
                      ) ?? r.buildingCandidates[0];
                    return (
                      <tr
                        key={r.lead.id}
                        onClick={() => setSelectedId(r.lead.id)}
                        className={isFocused ? "table-primary" : undefined}
                        style={{
                          cursor: "pointer",
                          borderLeft: isFocused
                            ? "3px solid #0d6efd"
                            : "3px solid transparent",
                        }}
                      >
                        <td
                          onClick={(e) => {
                            // Cell-sized click target. See LeadSearchPage
                            // for the rationale + the (e.target tag !==
                            // INPUT) dedupe.
                            e.stopPropagation();
                            if ((e.target as HTMLElement).tagName === "INPUT") {
                              return;
                            }
                            toggle(r.lead.id);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <Form.Check
                            checked={isSelected}
                            onChange={() => toggle(r.lead.id)}
                          />
                        </td>
                        <td>
                          <WithTooltip
                            text={leadValueTooltip(r.lead.leadValue)}
                          >
                            <Badge
                              {...leadValueBadgeProps(r.lead.leadValue)}
                              style={{ fontSize: 10 }}
                            >
                              {leadValueLabel(r.lead.leadValue)}
                            </Badge>
                          </WithTooltip>
                        </td>
                        <td>
                          {(() => {
                            const overall = r.lead.leadConfidence;
                            const fmt = (n: number | null): string =>
                              n == null ? "—" : `${Math.round(n)}%`;
                            const tooltip =
                              overall == null
                                ? "Overall confidence is not reported - the lead has no building, landlord, or tenant signals to score."
                                : [
                                    `Overall: ${fmt(overall)} (weakest of building / landlord / tenant; missing roles skipped)`,
                                    `• Building: ${fmt(r.buildingConfidence)}`,
                                    `• Landlord: ${fmt(r.landlordConfidence)}`,
                                    `• Tenant: ${fmt(r.tenantConfidence)}`,
                                  ].join("\n");
                            return (
                              <ConfidenceBadge
                                confidence={overall}
                                tooltip={tooltip}
                              />
                            );
                          })()}
                        </td>
                        <td>
                          <div style={{ fontSize: 13.5 }}>
                            {r.lead.address ? (
                              formatAddressCasing(r.lead.address)
                            ) : (
                              <em>(no address)</em>
                            )}
                            <EnrichmentTierBadge
                              premiumEnrichedAt={r.premiumEnrichedAt}
                            />
                          </div>
                          <div style={{ color: "#999", fontSize: 12.5 }}>
                            {r.lead.postcode}
                          </div>
                          {r.lead.matchedExistingSiteID && (
                            <Badge
                              bg="warning"
                              text="dark"
                              style={{ fontSize: 11.5 }}
                            >
                              On platform
                            </Badge>
                          )}
                          {r.lead.duplicateOfLeadID && (
                            <Badge
                              bg="warning"
                              text="dark"
                              style={{ fontSize: 11.5 }}
                            >
                              Duplicate of{" "}
                              {r.lead.duplicateOfLeadID.slice(0, 8)}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {(() => {
                            // A linked site's footprint (created/matched)
                            // is authoritative - show it in place of the
                            // lead's VOA/OSM candidate area. Falls back to
                            // the candidate area for unlinked leads.
                            const linkedSiteArea =
                              siteBuildingAreaByLeadId[r.lead.id] ?? null;
                            const polygonArea =
                              linkedSiteArea ??
                              effectiveBuilding?.surfaceAreaSqm ??
                              null;
                            if (
                              r.buildingCandidates.length === 0 &&
                              polygonArea == null
                            ) {
                              return <span style={{ color: "#aaa" }}>-</span>;
                            }
                            return (
                              <>
                                {polygonArea != null
                                  ? Math.round(polygonArea).toLocaleString()
                                  : "(no area)"}
                                {/* The "unsaved" marker tracks a mid-edit
                                    candidate pick; irrelevant when we're
                                    showing the saved site footprint. */}
                                {linkedSiteArea == null &&
                                  pending?.dirty &&
                                  pending.buildingBkey != null &&
                                  pending.buildingBkey !==
                                    r.initialBuildingBkey && (
                                    <span
                                      style={{
                                        color: "#888",
                                        fontSize: 10,
                                        marginLeft: 4,
                                      }}
                                    >
                                      unsaved
                                    </span>
                                  )}
                              </>
                            );
                          })()}
                        </td>
                        {(() => {
                          let name: React.ReactNode = (
                            <span style={{ color: "#aaa" }}>-</span>
                          );
                          let conf: number | null = null;
                          let confTooltip: string | undefined = undefined;
                          let unsaved = false;
                          if (pending && pending.dirty) {
                            if (pending.ownerOccupier) {
                              // OO: one org is both occupier and owner. Show
                              // the occupier name here; the Landlord column
                              // reads "Owner occupied".
                              const ooOrg =
                                pending.tenant ?? pending.landlord ?? null;
                              if (ooOrg?.displayName != null) {
                                name = ooOrg.displayName;
                                unsaved = true;
                                // Resolve the occupier's confidence from ITS OWN
                                // source list so the badge doesn't blank and
                                // agrees with the right panel. The owner org can
                                // sit in the tenant OR landlord slot, so branch
                                // by source like the sibling Landlord column -
                                // never fall back to a different company's match.
                                conf =
                                  ooOrg.source === "agentic"
                                    ? deriveAgenticConfidence(
                                        ooOrg,
                                        r.agenticMatches
                                      )
                                    : ooOrg.source === "hmlr"
                                      ? deriveLandlordConfidence(
                                          ooOrg,
                                          r.filteredHmlrMatches
                                        )
                                      : deriveTenantConfidence(
                                          ooOrg,
                                          r.tenantMatches
                                        );
                              }
                            } else if (pending.tenant) {
                              name = pending.tenant.displayName;
                              conf = deriveTenantConfidence(
                                pending.tenant,
                                r.tenantMatches
                              );
                              unsaved = true;
                            }
                          } else if (r.lead.ownerOccupier) {
                            // OO sites carry a single org that is both occupier
                            // and owner. Show the occupier (tenant) name here;
                            // the Landlord column reads "Owner occupied".
                            const ooName =
                              r.tenantName ?? r.landlordName ?? null;
                            if (ooName != null) {
                              name = ooName;
                              conf = r.tenantConfidence ?? r.landlordConfidence;
                            }
                          } else if (r.tenantName != null) {
                            name = r.tenantName;
                            conf = r.tenantConfidence;
                            confTooltip = buildTenantTooltip(
                              r.tenantMatches[0] ?? null
                            );
                          }
                          return (
                            <>
                              <td>
                                <ConfidenceBadge
                                  confidence={conf}
                                  tooltip={confTooltip}
                                />
                              </td>
                              <td>
                                {name}
                                {unsaved && (
                                  <span
                                    style={{
                                      color: "#888",
                                      fontSize: 11.5,
                                      marginLeft: 4,
                                    }}
                                  >
                                    unsaved
                                  </span>
                                )}
                              </td>
                            </>
                          );
                        })()}
                        {(() => {
                          let name: React.ReactNode = (
                            <span style={{ color: "#aaa" }}>-</span>
                          );
                          let conf: number | null = null;
                          let confTooltip: string | undefined = undefined;
                          let unsaved = false;
                          if (pending && pending.dirty) {
                            if (pending.ownerOccupier) {
                              // OO: the occupier name is in the Tenant column;
                              // this column carries the OO status instead.
                              name = (
                                <span style={{ color: "#6c757d" }}>
                                  {OWNER_OCCUPIED_LANDLORD_LABEL}
                                </span>
                              );
                            } else if (pending.landlord) {
                              name = pending.landlord.displayName;
                              conf =
                                pending.landlord.source === "agentic"
                                  ? deriveAgenticConfidence(
                                      pending.landlord,
                                      r.agenticMatches
                                    )
                                  : deriveLandlordConfidence(
                                      pending.landlord,
                                      r.filteredHmlrMatches
                                    );
                              unsaved = true;
                            }
                          } else if (r.lead.ownerOccupier) {
                            name = (
                              <span style={{ color: "#6c757d" }}>
                                {OWNER_OCCUPIED_LANDLORD_LABEL}
                              </span>
                            );
                          } else if (r.landlordName != null) {
                            name = r.landlordName;
                            conf = r.landlordConfidence;
                            confTooltip = buildLandlordTooltip(
                              r.hmlrMatches[0] ?? null
                            );
                          } else {
                            // No saved landlord and no HMLR top-pick in the
                            // filter. If landlord research has run, mirror the
                            // right-pane OrgSummary status instead of a bare "-"
                            // that reads as "research never ran". Options the
                            // right panel would show = filtered HMLR + agentic.
                            const agentic = (r.lead.agenticData ?? {}) as {
                              matches?: unknown[];
                              decision?: unknown;
                              researchedAt?: string | null;
                            };
                            const status = researchedBlankLandlordStatus(
                              {
                                agenticMatches: r.agenticMatches,
                                agenticDecision: agentic.decision,
                                agenticResearchedAt:
                                  agentic.researchedAt ?? null,
                              },
                              r.filteredHmlrMatches.length +
                                r.agenticMatches.length
                            );
                            if (status != null) {
                              name = (
                                <span style={{ color: "#6c757d" }}>
                                  {status}
                                </span>
                              );
                            }
                          }
                          return (
                            <>
                              <td>
                                <ConfidenceBadge
                                  confidence={conf}
                                  tooltip={confTooltip}
                                />
                              </td>
                              <td>
                                {name}
                                {unsaved && (
                                  <span
                                    style={{
                                      color: "#888",
                                      fontSize: 11.5,
                                      marginLeft: 4,
                                    }}
                                  >
                                    unsaved
                                  </span>
                                )}
                                {(() => {
                                  // Per-row research state: single run, in-flight bulk
                                  // sub-batch, still-pending chain lead, or a finished
                                  // chain's unreached ("not run") lead - via the shared
                                  // helper so /leads/search matches this exactly.
                                  const rs = researchRowChipState({
                                    ref: r.lead.id,
                                    singleResearchingRefs,
                                    bulkRefs: bulkResearchOp?.leadIds,
                                    bulkCurrent: bulkResearchCurrent,
                                    bulkCancelledRefs:
                                      bulkResearchCancelledRefs,
                                    chainPendingRefs:
                                      bulkResearchChainPendingRefs,
                                    notRunRefs: researchNotRunRefs,
                                    serverOperationKey:
                                      r.lead.researchOperationKey,
                                  });
                                  if (rs === "researching") {
                                    return (
                                      <span
                                        style={{
                                          color: "#0d6efd",
                                          fontSize: 11.5,
                                          marginLeft: 4,
                                        }}
                                      >
                                        researching
                                        {bulkResearchActiveSearches > 0
                                          ? ` (${bulkResearchActiveSearches})`
                                          : ""}
                                        …
                                      </span>
                                    );
                                  }
                                  if (rs === "queued") {
                                    return (
                                      <span
                                        style={{
                                          color: "#888",
                                          fontSize: 11.5,
                                          marginLeft: 4,
                                        }}
                                      >
                                        queued…
                                      </span>
                                    );
                                  }
                                  if (rs === "not-run") {
                                    return (
                                      <span
                                        style={{
                                          color: "#b8860b",
                                          fontSize: 11.5,
                                          marginLeft: 4,
                                        }}
                                      >
                                        not run ·{" "}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRerunResearch(r.lead.id);
                                          }}
                                          style={{
                                            padding: 0,
                                            border: "none",
                                            background: "none",
                                            color: "#0d6efd",
                                            fontSize: 11.5,
                                            cursor: "pointer",
                                          }}
                                        >
                                          run again
                                        </button>
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </td>
                            </>
                          );
                        })()}
                        <td style={{ textAlign: "right" }}>
                          {(() => {
                            // Leads with no drafts are omitted from the map
                            // rather than sent as 0, so the dash is ours to
                            // render - it reads better than a column of zeros.
                            const drafted =
                              outreachContactCountByLeadId[r.lead.id] ?? 0;
                            return drafted > 0 ? (
                              drafted
                            ) : (
                              <span style={{ color: "#adb5bd" }}>-</span>
                            );
                          })()}
                        </td>
                        <td>
                          {r.lead.tags.map((t) => (
                            <Badge
                              key={t}
                              bg="secondary"
                              style={{ marginRight: 2, fontWeight: 400 }}
                            >
                              {t}
                            </Badge>
                          ))}
                        </td>
                        <td>
                          {(() => {
                            // Prefer a site originated from this lead
                            // (created via bulk-create); fall back to a
                            // site the lead was matched against ("On
                            // platform" - existed pre-lead). Either way,
                            // the [Site] button takes the operator to
                            // the linked site's edit page. If a
                            // bulk-create is currently in flight for
                            // this lead, surface that instead of "-"
                            // so the operator knows not to retry.
                            const originatedSiteId =
                              siteIdsByLeadId[r.lead.id]?.[0] ?? null;
                            const linkedSiteId =
                              originatedSiteId ?? r.lead.matchedExistingSiteID;
                            if (linkedSiteId) {
                              return (
                                <a
                                  href={`/site/${linkedSiteId}/edit`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: 13.5 }}
                                >
                                  View site
                                </a>
                              );
                            }
                            const skipReason = bulkCreateSkippedByLeadId.get(
                              r.lead.id
                            );
                            if (skipReason) {
                              return (
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    color: "#d63384",
                                    fontWeight: 500,
                                  }}
                                  title={`Bulk-create skipped this lead: ${skipReason}. Fix the underlying issue and re-run.`}
                                >
                                  skipped: {skipReason}
                                </span>
                              );
                            }
                            if (pendingCreateLeadIds.has(r.lead.id)) {
                              return (
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    color: "#0d6efd",
                                    fontStyle: "italic",
                                  }}
                                  title="A bulk-create operation is currently turning this lead into a site. The link will appear here once the LRO finishes."
                                >
                                  creating...
                                </span>
                              );
                            }
                            return <span style={{ color: "#aaa" }}>-</span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ color: "#999", padding: 20 }}>
                        {fetchLeads.isLoading ? (
                          "Loading leads..."
                        ) : (
                          <>
                            No leads. Try{" "}
                            <Link to="/leads/search">searching for leads</Link>{" "}
                            or <Link to="/leads/import">importing a CSV</Link>.
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {showRight && (
          <div style={{ width: isCompact ? "100%" : "45%", height: "100%" }}>
            {researchError && (
              <Alert variant="danger" style={{ fontSize: 13, margin: 8 }}>
                {researchError}
              </Alert>
            )}
            {selectedRow ? (
              <LeadEditPanel
                // Leads pane: the operator maps to / creates the platform
                // Organisation before create-site (so platform-org shows),
                // and the decision is already made (so the "Then" runners-up
                // peek is hidden - still reachable via Change).
                mode="lead"
                // The inbox edits promoted leads, not search results: the
                // New/Viewed/Rejected cycle does not apply here.
                reviewStatus="no-status"
                // The panel is gone by the time an unmount-flush rejection
                // lands, so this is the only place it can still be surfaced.
                onFlushFailed={(message) => setResearchError(message)}
                // Remount when the focused row changes (or when the
                // BE rewrote this row via refetch / premium-enrich).
                // LeadEditPanel has a lot of useState-initialised-
                // from-props internal state (default building,
                // selected landlord/tenant, dirty flag) that doesn't
                // react to prop changes; without a key the previous
                // row's edits leak into the next row's panel and
                // pending-state emit, blanking that row's left-cell
                // tenant/landlord display. The same remount path is
                // also what makes "discard and leave?" actually
                // discard - the panel re-initialises from the new
                // row's props instead of carrying the dirty state
                // across the navigation.
                // The `site` vs `lead` suffix forces a remount once the
                // linked site's buildings finish loading, so the panel
                // re-initialises its default-building state from the site
                // footprint instead of the lead's VOA/OSM candidates.
                key={`${selectedRow.lead.id}|${panelKeyStamp}|${
                  siteBuildingCandidates ? "site" : "lead"
                }`}
                address={selectedRow.lead.address}
                leadTags={selectedRow.lead.tags}
                proximityContext={selectedRow.lead.proximityContext}
                // When the lead has a linked site, show that site's
                // building(s) as the authoritative footprint instead of the
                // lead's raw candidates (see siteBuildingCandidates above).
                buildingCandidates={
                  siteBuildingCandidates ?? selectedRow.buildingCandidates
                }
                hmlrMatches={selectedRow.hmlrMatches}
                tenantMatches={selectedRow.tenantMatches}
                initialBuildingBkey={
                  siteBuildingCandidates
                    ? siteBuildingCandidates[0].bkey
                    : selectedRow.initialBuildingBkey
                }
                initialLandlord={selectedRow.initialLandlord}
                initialTenant={selectedRow.initialTenant}
                initialOwnerOccupier={selectedRow.lead.ownerOccupier}
                initialHasPv={selectedRow.lead.hasPv}
                siteExistingPvKwp={
                  siteExistingPvKwpByLeadId[selectedRow.lead.id] ?? null
                }
                fallbackLat={selectedRow.lead.latitude}
                fallbackLon={selectedRow.lead.longitude}
                inputContext={buildLeadInputContext(selectedRow.lead)}
                enrichmentFetchedAt={buildEnrichmentFetchedAt(selectedRow.lead)}
                onSave={handleSave}
                onRecountCommercial={() =>
                  recountCommercial({ leadIds: [selectedRow.lead.id] })
                }
                recountingCommercial={recountingCommercial}
                onSaveOrgDecision={saveOrgDecisionReportingErrors}
                onSaveHasPv={saveHasPvReportingErrors}
                onCreateSite={
                  selectedRow.lead.matchedExistingSiteID == null
                    ? handleCreateSiteForLead
                    : undefined
                }
                createSiteDisabledReason={
                  selectedRow.buildingCandidates.length === 0
                    ? "This lead has no building polygon - resolve a polygon on the right panel before creating a site."
                    : pendingSelectionByLeadId[selectedRow.lead.id]?.dirty
                      ? "Wait for your changes to save - Create site uses the saved building, owner-occupier and org selections, not the unsaved ones in this panel."
                      : undefined
                }
                linkedSite={(() => {
                  const siteId = selectedLinkedSiteId;
                  if (!siteId) return null;
                  // Pull names from the saved decisions payload; if
                  // ownerOccupier the same org sits on both rows.
                  const tenantPayload = (selectedRow.lead.tenantDecision
                    ?.payload ?? null) as Record<string, unknown> | null;
                  const landlordPayload = (selectedRow.lead.landlordDecision
                    ?.payload ?? null) as Record<string, unknown> | null;
                  return {
                    leadId: selectedRow.lead.id,
                    siteId,
                    siteUrl: `/site/${siteId}/edit`,
                    tenantName:
                      (tenantPayload?.displayName as string | undefined) ??
                      null,
                    landlordName:
                      (landlordPayload?.displayName as string | undefined) ??
                      null,
                    // Total footprint of the linked site's building(s) - the
                    // panel shows this in place of the lead's declared area.
                    totalBuildingArea: linkedSiteData?.totalBuildingArea,
                    installCapacityKwp:
                      siteInstallCapacityKwpByLeadId[selectedRow.lead.id],
                  };
                })()}
                onRemoveFromLeads={handleRemoveFromLeads}
                onRefetch={handleLeadRefetch}
                refetching={refetchLead.isLoading}
                onReenrichAddress={handleReenrichAddress}
                reenriching={reenrichLeadAddress.isLoading}
                onPremiumEnrich={handleLeadPremiumEnrich}
                premiumEnriching={premiumEnrichLead.isLoading}
                onResearchLandlord={handleResearchLandlord}
                // "Researching" is the ACTIVE state only - a run covering this lead,
                // or the bulk lead currently being worked. A bulk lead still waiting
                // its turn is `researchQueued`, so the button reads "Queued" not
                // "Researching". Read off the one derived state the list cell uses,
                // never the kickoff mutation's flag - that is global to the page, so
                // it made EVERY open panel claim to be researching while any other
                // row's research was being started.
                researching={selectedResearchState === "researching"}
                researchQueued={selectedResearchState === "queued"}
                onCancelQueuedResearch={
                  selectedResearchState === "queued"
                    ? () => handleCancelQueuedResearch(selectedRow.lead.id)
                    : undefined
                }
                // Live "(N searches run)" for whichever row is open, from that row's
                // own operation - not only the run this tab launched last. The bulk
                // fallback covers the batch's active row, whose count comes from the
                // batch poll rather than a per-row one.
                researchSearchCount={
                  selectedResearchSearchCount ??
                  (bulkResearchOp?.leadIds[bulkResearchCurrent] ===
                  selectedRow.lead.id
                    ? bulkResearchActiveSearches
                    : null)
                }
                agenticMatches={
                  (
                    selectedRow.lead.agenticData as
                      | { matches?: AgenticMatch[] }
                      | null
                      | undefined
                  )?.matches ?? undefined
                }
                tenantCommercial={selectedRow.lead.tenantCommercial}
                leadValue={selectedRow.lead.leadValue}
                agenticResearchedAt={
                  (
                    selectedRow.lead.agenticData as
                      | { researchedAt?: string }
                      | null
                      | undefined
                  )?.researchedAt ?? null
                }
                agenticDecision={
                  (
                    selectedRow.lead.agenticData as
                      | { decision?: AgenticDecision }
                      | null
                      | undefined
                  )?.decision ?? null
                }
                researchTraceRunId={
                  (
                    selectedRow.lead.agenticData as
                      | { traceRunId?: string }
                      | null
                      | undefined
                  )?.traceRunId ?? null
                }
                researchSubjectRef={selectedRow.lead.id}
                peopleDiscovery={{
                  leadId: selectedRow.lead.id,
                  landlordDecision: selectedRow.lead.landlordDecision,
                  tenantDecision: selectedRow.lead.tenantDecision,
                  contactCandidates: selectedRow.lead.contactCandidates,
                  contactEnrichments: selectedRow.lead.contactEnrichments,
                  hubspotMatches: selectedRow.lead.hubspotMatches,
                }}
                onPendingSelectionChange={(pending) => {
                  // Stash the right panel's mid-edit selection on the
                  // currently-focused row so the left-table cells can
                  // render it. Cleared when the operator picks a
                  // different row (or the panel remounts via key
                  // change after a refetch). Same shape + cadence as
                  // /leads/search.
                  if (!selectedRow) return;
                  setPendingSelectionByLeadId((prev) => ({
                    ...prev,
                    [selectedRow.lead.id]: pending,
                  }));
                }}
                warnings={
                  fetchLeads.error ? (
                    <Alert variant="danger">Failed to fetch leads.</Alert>
                  ) : null
                }
              />
            ) : (
              <LeadsOverviewMap pins={pins} onPinClick={setSelectedId} />
            )}
          </div>
        )}
      </div>

      <Modal show={premiumModalOpen} onHide={closePremiumModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Enrich{" "}
            {premiumOperation
              ? premiumOperation.leadCount
              : premiumEligibleSelected.length}{" "}
            lead
            {(premiumOperation
              ? premiumOperation.leadCount
              : premiumEligibleSelected.length) === 1
              ? ""
              : "s"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!premiumOperation && (
            <>
              <p>
                <strong>{premiumEligibleSelected.length}</strong> lead
                {premiumEligibleSelected.length === 1 ? "" : "s"} would have
                premium enrichment ({PREMIUM_ENRICHMENT_SERVICES}) run.
              </p>
              {selected.size > premiumEligibleSelected.length && (
                <p>
                  {selected.size - premiumEligibleSelected.length} lead
                  {selected.size - premiumEligibleSelected.length === 1
                    ? " has"
                    : "s have"}{" "}
                  already been enriched (or are not VOA-sourced) and will be
                  skipped.
                </p>
              )}
              {premiumEligibleSelected.length >
                BULK_PREMIUM_ENRICH_MAX_PER_OPERATION && (
                <Alert variant="warning" style={{ fontSize: 13 }}>
                  At most {BULK_PREMIUM_ENRICH_MAX_PER_OPERATION} leads per
                  operation; you have {premiumEligibleSelected.length} eligible.
                  Reduce the selection or run multiple batches.
                </Alert>
              )}
              <p>
                Estimated time:{" "}
                <strong>
                  {formatEtaSeconds(
                    Math.max(3, premiumEligibleSelected.length * 1)
                  )}
                </strong>{" "}
                (two Google calls per lead, fanned out across 24 parallel
                workers).
              </p>
              <p>
                Estimated cost:{" "}
                <strong>
                  ~${(premiumEligibleSelected.length * 0.01).toFixed(2)}
                </strong>{" "}
                (minus any remaining free monthly allowance).
              </p>
            </>
          )}
          {premiumOperation &&
            !premiumOperationResult &&
            premiumOperationStatus !== "failed" && (
              <div style={{ fontSize: 13 }}>
                <p style={{ marginBottom: 6 }}>
                  Enriching <strong>{premiumOperation.leadCount}</strong> lead
                  {premiumOperation.leadCount === 1 ? "" : "s"}. The operation
                  is running on the BE - it&apos;s safe to close this modal, the
                  refreshed leads will appear in the table once complete.
                </p>
                {premiumOperationProgress &&
                  premiumOperationProgress.total > 0 && (
                    <>
                      <p style={{ marginBottom: 4, color: "#555" }}>
                        {premiumOperationProgress.message} (
                        {premiumOperationProgress.current}/
                        {premiumOperationProgress.total})
                      </p>
                      <div
                        style={{
                          background: "#e9ecef",
                          borderRadius: 4,
                          height: 8,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: "#198754",
                            width: `${
                              (100 * premiumOperationProgress.current) /
                              premiumOperationProgress.total
                            }%`,
                            height: "100%",
                            transition: "width 200ms ease",
                          }}
                        />
                      </div>
                    </>
                  )}
              </div>
            )}
          {premiumOperation && premiumOperationStatus === "failed" && (
            <Alert variant="danger" style={{ fontSize: 13 }}>
              Premium enrichment failed:{" "}
              {premiumOperationPoll.data?.operation?.error ??
                "see CloudWatch logs for details"}
              .
            </Alert>
          )}
          {premiumOperationResult && (
            <>
              <p style={{ fontSize: 13 }}>
                <strong>
                  {premiumOperationResult.leads.length} lead
                  {premiumOperationResult.leads.length === 1 ? "" : "s"}
                </strong>{" "}
                enriched.
                {premiumOperationResult.skipped.length > 0 &&
                  ` ${premiumOperationResult.skipped.length} skipped.`}
              </p>
              {premiumOperationResult.skipped.length > 0 && (
                <ul style={{ fontSize: 12, color: "#999", paddingLeft: 24 }}>
                  {premiumOperationResult.skipped.map((s) => (
                    <li key={s.leadId}>
                      {s.leadId.slice(0, 8)}: {s.reason}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {premiumError && (
            <Alert variant="danger" style={{ fontSize: 13 }}>
              {premiumError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!premiumOperation ? (
            <>
              <Button variant="secondary" onClick={closePremiumModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmPremium}
                disabled={
                  bulkPremiumEnrichLeads.isLoading ||
                  premiumEligibleSelected.length === 0 ||
                  premiumEligibleSelected.length >
                    BULK_PREMIUM_ENRICH_MAX_PER_OPERATION
                }
              >
                {bulkPremiumEnrichLeads.isLoading
                  ? "Starting..."
                  : `Enrich ${premiumEligibleSelected.length} lead${
                      premiumEligibleSelected.length === 1 ? "" : "s"
                    }`}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={closePremiumModal}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={tagModalOpen !== null}
        onHide={() => {
          setTagModalOpen(null);
          setTagsToRemove(new Set());
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {tagModalOpen === "add" ? "Add tag" : "Remove tag"} -{" "}
            {selected.size} lead{selected.size === 1 ? "" : "s"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tagModalOpen === "remove" ? (
            tagsOnSelectedLeads.length === 0 ? (
              <p style={{ color: "#666" }}>
                None of the selected leads have any tags.
              </p>
            ) : (
              <>
                <Form.Label>
                  Tap to select tags to remove from the {selected.size} selected
                  lead
                  {selected.size === 1 ? "" : "s"}.
                </Form.Label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {tagsOnSelectedLeads.map((t) => {
                    const active = tagsToRemove.has(t);
                    return (
                      <Button
                        key={t}
                        size="sm"
                        variant={active ? "danger" : "outline-secondary"}
                        style={{ fontSize: 12, padding: "3px 10px" }}
                        onClick={() =>
                          setTagsToRemove((prev) => {
                            const next = new Set(prev);
                            if (next.has(t)) next.delete(t);
                            else next.add(t);
                            return next;
                          })
                        }
                      >
                        {active ? "✕ " : ""}
                        {t}
                      </Button>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            <>
              {topTagChips.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginBottom: 6,
                  }}
                >
                  {topTagChips.map((t) => (
                    <Button
                      key={t}
                      size="sm"
                      variant="outline-secondary"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      onClick={() => addTagChipToInput(t)}
                      title="Click to add this tag to the input"
                    >
                      + {t}
                    </Button>
                  ))}
                </div>
              )}
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="HSBC, BS11, q2-2026"
                value={tagModalInput}
                onChange={(e) => setTagModalInput(e.target.value)}
              />
              <Form.Text muted>
                Comma- or newline-separated. Adding a tag a lead already has is
                a no-op (de-duped server-side).
              </Form.Text>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setTagModalOpen(null);
              setTagsToRemove(new Set());
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkTags}
            disabled={
              bulkUpdateLeadTags.isLoading ||
              (tagModalOpen === "add"
                ? !tagModalInput.trim()
                : tagsToRemove.size === 0)
            }
          >
            {bulkUpdateLeadTags.isLoading ? "Saving..." : "Apply"}
          </Button>
        </Modal.Footer>
      </Modal>

      <BulkCreateSitesModal
        controller={bulkCreate}
        skippedWarnings={{
          noPolygon: noPolygonSelected.length,
          noPolygonDecision: incompleteSelected.length,
        }}
      />

      <LeadsHelpModal
        show={helpOpen}
        variant="leads"
        onHide={() => setHelpOpen(false)}
      />
    </Container>
  );
};

export default LeadsPage;
